import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SuperAdminSidebar from '@/components/SuperAdminSidebar';
import InstallAppBanner from '@/components/InstallAppBanner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  BarChart3, Users, School, Shield, Activity, Search, ChevronDown, ChevronUp,
  Edit2, UserCheck, UserX, Eye, Plus, Building2, Lock, Unlock, FileText, X, Check, Save,
  CreditCard, AlertTriangle, Bell, Send, Calendar, DollarSign,
  Wrench, ToggleLeft, ToggleRight, HeartPulse, Database, Play, RotateCcw, Zap, Pause,
  ScrollText, Tag, ArrowUpRight, Presentation, FlaskConical, Megaphone, ShieldCheck, Printer, Download, ImageIcon,
  HardDrive, Trash2, RefreshCw, Cpu, TrendingUp, Package, ChevronRight, Loader2, CheckCircle, Upload, GraduationCap, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import PresentationPlaybook from '@/components/PresentationPlaybook';
import DemoDataManager from '@/components/DemoDataManager';
import DemoLeadsTab from '@/components/DemoLeadsTab';
import DemoAccountsTab from '@/components/DemoAccountsTab';
import ABTestingTab from '@/components/ABTestingTab';
import TeamUpdatesTab from '@/components/TeamUpdatesTab';
import LaunchReadinessTab from '@/components/LaunchReadinessTab';
import PrintMaterialsTab from '@/pages/PrintMaterials';
import ProficiencyWatch from '@/pages/ProficiencyWatch';
import UsbOrdersQuickStats from '@/components/UsbOrdersQuickStats';
import LiveDataGuard from '@/pages/LiveDataGuard';
import ParityAudit from '@/pages/ParityAudit';
import TeacherPortalUsersTab from '@/components/TeacherPortalUsersTab';
import DownloadActivityTab from '@/components/DownloadActivityTab';
import QuotesManagementTab from '@/components/QuotesManagementTab';
import OrdersTab from '@/components/OrdersTab';
import AnomalyReportTab from '@/components/AnomalyReportTab';
import ContentStudioPreview from '@/components/ContentStudioPreview';
import CommissionTrackerTab from '@/components/CommissionTrackerTab';
import SalesCollateralTab from '@/components/SalesCollateralTab';
import BigPictureGalleryTab from '@/components/BigPictureGalleryTab';
import InfrastructureGuideTab from '@/components/InfrastructureGuideTab';
import BetaManagementTab from '@/components/BetaManagementTab';
import LicensesTab from '@/components/LicensesTab';
import ERMAComplianceTab from '@/components/ERMAComplianceTab';
import BenchmarkingTab from '@/components/BenchmarkingTab';
import ContentAuthenticityAuditor from '@/components/ContentAuthenticityAuditor';
import CoverageDashboardTab from '@/components/CoverageDashboardTab';
import PrintProductionTab from '@/components/PrintProductionTab';
import MaintenanceTab from '@/components/MaintenanceTab';
import SemanticAuditReviewTab from '@/components/SemanticAuditReviewTab';
import SalesRepsManagementTab from '@/components/SalesRepsManagementTab';
import SampleDemosTab from '@/components/SampleDemosTab';
import SocialMediaStudioTab from '@/components/SocialMediaStudioTab';
import SupplementalSolutionsTab from '@/components/SupplementalSolutionsTab';
import RestorePointsTab from '@/components/RestorePointsTab';
import VariantAuthoringTab from '@/components/VariantAuthoringTab';
import SummativeAuthoringTab from '@/components/SummativeAuthoringTab';
import BPContentFitAudit from '@/components/BPContentFitAudit';
import ProficiencySpiderChart from '@/components/ProficiencySpiderChart';
import AuthContext from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LICENSE_TIERS = {
  'K-5': { label: 'Elementary (K-5)', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  'K-8': { label: 'Elem + Middle (K-8)', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  '6-8': { label: 'Middle (6-8)', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  '9-12': { label: 'High School (9-12)', color: 'bg-purple-100 text-purple-800 border-purple-300' },
  'all': { label: 'All Grades (K-12)', color: 'bg-indigo-100 text-indigo-800 border-indigo-300' },
};

const ROLE_COLORS = {
  super_admin: 'bg-red-100 text-red-800',
  district_admin: 'bg-indigo-100 text-indigo-800',
  school_admin: 'bg-blue-100 text-blue-800',
  teacher: 'bg-green-100 text-green-800',
  student: 'bg-gray-100 text-gray-700',
};

// ========== DISK STATUS WIDGET ==========
const DiskStatusWidget = () => {
  const [diskData, setDiskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleaning, setCleaning] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDiskStatus = async (showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    try {
      const token = localStorage.getItem('mosaic_session_token');
      const res = await axios.get(`${API}/blm/disk-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiskData(res.data);
      if (showSpinner) toast.success('Disk status refreshed');
    } catch {
      setDiskData(null);
      if (showSpinner) toast.error('Failed to load disk status');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchDiskStatus(false); }, []);

  const handleCleanup = async () => {
    setCleaning(true);
    try {
      const token = localStorage.getItem('mosaic_session_token');
      const res = await axios.post(`${API}/blm/cache-cleanup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { files_deleted, total_freed_mb } = res.data;
      if (files_deleted > 0) {
        toast.success(`Freed ${total_freed_mb}MB (${files_deleted} cached PDFs removed)`);
      } else {
        toast.info('Cache is already clean — no assessment PDFs to remove');
      }
      await fetchDiskStatus(false);
    } catch {
      toast.error('Cache cleanup failed');
    } finally {
      setCleaning(false);
    }
  };

  if (loading) return null;
  if (!diskData) return null;

  const { disk, cache, status, warnings } = diskData;
  const pct = disk.percent_used;
  const barColor = status === 'critical' ? 'bg-red-500' : status === 'warning' ? 'bg-amber-500' : 'bg-emerald-500';
  const statusColor = status === 'critical' ? 'text-red-700 bg-red-50 border-red-200' : status === 'warning' ? 'text-amber-700 bg-amber-50 border-amber-200' : 'text-emerald-700 bg-emerald-50 border-emerald-200';

  return (
    <div className="border-2 border-gray-100 p-5" data-testid="disk-status-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-sm flex items-center gap-1.5">
          <HardDrive size={14} className="text-gray-500" /> System Health
        </h3>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase border rounded-sm ${statusColor}`} data-testid="disk-status-badge">
            {status}
          </span>
          <button
            onClick={() => fetchDiskStatus(true)}
            disabled={refreshing}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
            data-testid="disk-refresh-btn"
            title="Read-only refresh of disk usage + cache size. No DB writes, no file changes."
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Disk bar */}
      <div className="mb-3">
        <div className="flex justify-between text-[11px] text-gray-500 mb-1">
          <span>Disk Usage</span>
          <span className="font-semibold text-gray-700">{pct}% ({disk.free_mb}MB free)</span>
        </div>
        <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${Math.min(pct, 100)}%` }} data-testid="disk-usage-bar" />
        </div>
      </div>

      {/* Cache info */}
      <div className="flex items-center justify-between text-[11px]">
        <div className="text-gray-500">
          PDF Cache: <span className="font-semibold text-gray-700">{cache.size_mb}MB</span> / {cache.max_mb}MB ({cache.files_cached} files)
        </div>
        <button
          onClick={handleCleanup}
          disabled={cleaning}
          className="flex items-center gap-1 text-[10px] px-2 py-1 bg-gray-50 hover:bg-red-50 text-gray-600 hover:text-red-600 border border-gray-200 hover:border-red-200 rounded transition-colors disabled:opacity-50"
          data-testid="disk-cleanup-btn"
          title="Removes cached BLM PDFs only. Auto-regenerated on next download."
        >
          {cleaning ? <RefreshCw size={10} className="animate-spin" /> : <Trash2 size={10} />}
          {cleaning ? 'Cleaning...' : 'Clean Cache'}
        </button>
      </div>

      {/* Production-impact disclosure (v6.32.23) — explicit list of what
          Clean Cache does and does NOT touch, so future Super Admins don't
          have to wonder. Replaces the previous one-line generic note. */}
      <details className="mt-2 group" data-testid="disk-cleanup-disclosure">
        <summary className="text-[10px] text-gray-500 cursor-pointer hover:text-gray-700 select-none list-none flex items-center gap-1">
          <ChevronRight size={10} className="transition-transform group-open:rotate-90" />
          What does Clean Cache actually do?
        </summary>
        <div className="mt-2 pl-3 border-l-2 border-gray-100 space-y-2 text-[10px] leading-relaxed">
          <div>
            <span className="font-semibold text-emerald-700 uppercase tracking-wide">Removes:</span>
            <span className="text-gray-600"> Cached BLM print booklet PDFs (5–12&nbsp;MB each) from this pod's <code className="bg-gray-50 px-1">/blm_downloads/</code> dir. They auto-regenerate on next download (10–30&nbsp;sec).</span>
          </div>
          <div>
            <span className="font-semibold text-rose-700 uppercase tracking-wide">Does NOT touch:</span>
            <ul className="text-gray-600 mt-0.5 space-y-0.5 ml-2">
              <li>• MongoDB (students, responses, assessments, schools, users, licenses, quotes, orders, audit logs)</li>
              <li>• In-progress exams or running audits/regen batches</li>
              <li>• Big Picture libraries (<code className="bg-gray-50 px-1">/bp_images/</code>, <code className="bg-gray-50 px-1">/bp_review/</code>, <code className="bg-gray-50 px-1">/cultural_images/</code>)</li>
              <li>• Marketing collateral PDFs (sell sheet, procurement letters, kit spec sheet)</li>
              <li>• User sessions (no one is logged out)</li>
            </ul>
          </div>
          <div className="text-gray-400 italic">
            Multi-pod note: prod runs replicas=2. Each click cleans only the pod that handled the request; LRU eviction handles the other pod automatically at 400&nbsp;MB.
          </div>
        </div>
      </details>

      {/* Warnings */}
      {warnings?.length > 0 && (
        <div className="mt-2 space-y-1">
          {warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 px-2 py-1 rounded">
              <AlertTriangle size={11} className="mt-0.5 shrink-0" />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ========== STAT CARD ==========
const StatCard = ({ icon: Icon, value, label, accent, onClick }) => (
  <div 
    className={`bg-white border-2 border-gray-100 p-5 ${onClick ? 'cursor-pointer hover:bg-gray-50 hover:border-blue-200 transition-colors' : ''}`}
    onClick={onClick}
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <Icon size={22} className={accent || 'text-[#002FA7]'} />
    <div className="text-3xl font-black mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</div>
    <div className="text-xs text-gray-500 font-medium mt-0.5">{label}</div>
  </div>
);

// ========== OVERVIEW TAB ==========
const OverviewTab = ({ stats, onNavigate, onRefresh }) => {
  const [spiderData, setSpiderData] = useState({ districts: [], platform_average: {} });
  const [repairBusy, setRepairBusy] = useState(false);
  const [repairResult, setRepairResult] = useState(null);  // {repaired, failed, totals}
  // v6.32.46 — Parity audit UI state
  const [parityBusy, setParityBusy] = useState(false);
  const [parityReportId, setParityReportId] = useState(null);
  const [parityProgress, setParityProgress] = useState(null);  // {phase, digital_progress, print_inventory_progress}
  const [parityReport, setParityReport] = useState(null);
  // v6.32.48 — Pictograph coverage UI state
  const [pictoCov, setPictoCov] = useState(null);
  const [pictoBusy, setPictoBusy] = useState(false);

  const loadPictoCoverage = async () => {
    setPictoBusy(true);
    try {
      const r = await axios.get(`${API}/bp-auditor/pictograph-coverage`);
      setPictoCov(r.data);
    } catch (e) {
      toast.error(`Coverage load failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setPictoBusy(false);
    }
  };

  // Auto-load coverage report on mount so the operator sees the gap
  // immediately without an extra click.
  useEffect(() => { loadPictoCoverage(); }, []);

  // Load most-recent completed parity report on mount so the panel
  // remembers what it last proved without forcing a re-run.
  useEffect(() => {
    axios.get(`${API}/bp-auditor/visual-fix/parity-audit/latest`)
      .then(res => {
        if (res.data?.report) setParityReport(res.data.report);
      })
      .catch(() => {});
  }, []);

  // Poll progress while a parity audit is in flight.
  useEffect(() => {
    if (!parityBusy || !parityReportId) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/bp-auditor/visual-fix/parity-audit/status?report_id=${parityReportId}`);
        setParityProgress(r.data);
        if (r.data.status === 'completed') {
          setParityReport(r.data);
          setParityBusy(false);
          toast.success('Parity audit complete.');
        } else if (r.data.status === 'failed') {
          setParityBusy(false);
          toast.error(`Parity audit failed: ${r.data.error || 'unknown'}`);
        }
      } catch (_) { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
  }, [parityBusy, parityReportId]);

  // v6.32.49 — One-click K-2 color regen state + polling.
  const [k2RegenBusy, setK2RegenBusy] = useState(false);
  const [k2RegenStatus, setK2RegenStatus] = useState(null);  // {done,total,ok,fail,skip,current,status}

  // v6.32.53 — Print-shop package selector state.
  const [printShopFiles, setPrintShopFiles] = useState([]);
  const [printShopSelected, setPrintShopSelected] = useState('');
  const [printShopMode, setPrintShopMode] = useState('print_resale');
  const [printShopSchool, setPrintShopSchool] = useState('');
  const [printShopOrder, setPrintShopOrder] = useState('');
  const [printShopBusy, setPrintShopBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/blm/print-shop-package/manifest`).then(r => {
      setPrintShopFiles(r.data?.filenames || []);
      if (r.data?.filenames?.length && !printShopSelected) {
        setPrintShopSelected(r.data.filenames[0].filename);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadPrintShopPackage = async () => {
    if (!printShopSelected) {
      toast.error('Pick a BLM filename first.');
      return;
    }
    setPrintShopBusy(true);
    try {
      const params = new URLSearchParams({
        filename: printShopSelected,
        mode: printShopMode,
        school_name: printShopSchool,
        order_number: printShopOrder,
      });
      // Use axios with blob response so we get the raw bytes
      const res = await axios.get(`${API}/blm/print-shop-package?${params}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const suffix = printShopMode === 'print_resale' ? 'PRINT' : printShopMode === 'sample' ? 'SAMPLE' : 'LICENSED';
      a.href = url;
      a.download = printShopSelected.replace('.pdf', `_${suffix}.pdf`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${printShopSelected} (${printShopMode}).`);
    } catch (e) {
      toast.error(`Download failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setPrintShopBusy(false);
    }
  };

  // v6.32.54 — Password-Protected Master Print Archive (single AES-256
  // ZIP containing every K-12 BLM PDF for emailing/disking to a print
  // shop). Builds detached on the backend; we poll for progress.
  const [pmaPassword, setPmaPassword] = useState('');
  const [pmaConfirm, setPmaConfirm] = useState('');
  const [pmaEdition, setPmaEdition] = useState('2026.1');
  const [pmaSchool, setPmaSchool] = useState('Print Shop Master Archive');
  const [pmaOrder, setPmaOrder] = useState('');
  const [pmaStarting, setPmaStarting] = useState(false);
  const [pmaJob, setPmaJob] = useState(null);

  // Load latest job on mount so the operator sees state across reloads.
  useEffect(() => {
    axios.get(`${API}/blm/print-master-archive/status`)
      .then(r => setPmaJob(r.data?.job || null))
      .catch(() => {});
  }, []);

  // Poll while running.
  useEffect(() => {
    const isRunning = pmaJob && pmaJob.status === 'running';
    if (!isRunning) return;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/blm/print-master-archive/status`, {
          params: { job_id: pmaJob.job_id },
        });
        setPmaJob(r.data?.job || null);
      } catch (e) { /* swallow — we'll retry on the next tick */ }
    }, 4000);
    return () => clearInterval(id);
  }, [pmaJob]);

  const startMasterArchive = async () => {
    if (pmaPassword.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }
    if (pmaPassword !== pmaConfirm) {
      toast.error('Passwords do not match.');
      return;
    }
    if (!window.confirm(
      `Build the AES-256-encrypted master archive of all 112 K-12 BLM PDFs?\n\n` +
      `Password: ${pmaPassword.replace(/./g, '•')} (${pmaPassword.length} chars)\n` +
      `Edition: ${pmaEdition}\n\n` +
      `IMPORTANT: copy the password somewhere safe — it is NOT stored on the server.\n` +
      `If you lose it, the archive cannot be opened and you must rebuild.\n\n` +
      `Estimated wall time: ~6-10 min. The build runs in the background; you can leave the page.`
    )) return;

    setPmaStarting(true);
    try {
      const r = await axios.post(`${API}/blm/print-master-archive/start`, {
        password: pmaPassword,
        edition: pmaEdition,
        school_name: pmaSchool,
        order_number: pmaOrder,
      });
      toast.success('Master archive build started.');
      // Clear the password fields after kickoff so it isn't sitting in
      // the DOM. The operator was warned to copy it before clicking.
      setPmaPassword('');
      setPmaConfirm('');
      // Pull initial status so the polling effect kicks in.
      const s = await axios.get(`${API}/blm/print-master-archive/status`, {
        params: { job_id: r.data.job_id },
      });
      setPmaJob(s.data?.job || null);
    } catch (e) {
      toast.error(`Start failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setPmaStarting(false);
    }
  };

  const downloadMasterArchive = async () => {
    if (!pmaJob || pmaJob.status !== 'completed') return;
    try {
      // v6.32.55 — fetch a signed short-lived URL and let the browser
      // stream the download natively. The previous axios+blob path
      // buffered the entire 700+ MB ZIP in JS memory which OOM'd the
      // tab on most laptops and tripped K8s ingress timeouts.
      const t = await axios.post(
        `${API}/blm/print-master-archive/download-token`,
        null,
        { params: { job_id: pmaJob.job_id } }
      );
      const relativeUrl = t.data?.download_url;
      if (!relativeUrl) throw new Error('No signed URL returned.');
      // The token URL is relative-rooted at /api; prepend backend host.
      const backendOrigin = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const fullUrl = `${backendOrigin}${relativeUrl}`;
      // Native download: the browser handles streaming + disk write.
      const a = document.createElement('a');
      a.href = fullUrl;
      a.download = pmaJob.archive_filename || 'MOSAIC_PrintMaster.zip';
      // Some browsers ignore .download on cross-origin URLs; the
      // Content-Disposition header on the response forces attachment.
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Download started — large archive may take a moment.');
    } catch (e) {
      toast.error(`Download failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  // v6.32.57 — CR-01 Image Source Audit (READ-ONLY visibility).
  // Tooling-only at this stage: surfaces the 70%-Unsplash sourcing
  // risk so the operator can plan a content-team review. NEVER
  // mutates question records.
  const [isaSummary, setIsaSummary] = useState(null);
  const [isaLoading, setIsaLoading] = useState(false);
  const [isaFilterClass, setIsaFilterClass] = useState('unsplash');
  const [isaFilterGrade, setIsaFilterGrade] = useState('');
  const [isaFilterModality, setIsaFilterModality] = useState('');
  const [isaQuestions, setIsaQuestions] = useState([]);
  const [isaQuestionsTotal, setIsaQuestionsTotal] = useState(0);
  const [isaUploading, setIsaUploading] = useState(false);
  const [isaManifests, setIsaManifests] = useState([]);

  const loadIsaSummary = async () => {
    setIsaLoading(true);
    try {
      const r = await axios.get(`${API}/image-source-audit/summary`);
      setIsaSummary(r.data);
    } catch (e) {
      toast.error(`Audit summary failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setIsaLoading(false);
    }
  };

  const loadIsaQuestions = async () => {
    try {
      const params = { classification: isaFilterClass, skip: 0, limit: 50 };
      if (isaFilterGrade) params.grade = isaFilterGrade;
      if (isaFilterModality) params.modality = isaFilterModality;
      const r = await axios.get(`${API}/image-source-audit/questions`, { params });
      setIsaQuestions(r.data?.questions || []);
      setIsaQuestionsTotal(r.data?.total_matching || 0);
    } catch (e) {
      toast.error(`Question fetch failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const loadIsaManifests = async () => {
    try {
      const r = await axios.get(`${API}/image-source-audit/manifests`);
      setIsaManifests(r.data?.manifests || []);
    } catch (e) {
      // Silent — endpoint returns [] when no manifests yet.
    }
  };

  // Auto-load summary + manifests on mount.
  useEffect(() => {
    loadIsaSummary();
    loadIsaManifests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadIsaManifest = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Manifest must be a .csv file.');
      return;
    }
    if (!window.confirm(
      `Upload "${file.name}" as a BVAF replacement manifest?\n\n` +
      `IMPORTANT: this is QUARANTINE only. The CSV will be parked for ` +
      `content-team review. NO question records will be modified by this upload. ` +
      `A separate apply step (not yet built) will perform the backfill ` +
      `after content-team sign-off.`
    )) {
      e.target.value = '';
      return;
    }
    setIsaUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API}/image-source-audit/upload-manifest`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(
        `Manifest stored (id=${r.data.manifest_id.slice(0, 8)}…, ` +
        `${r.data.row_count} rows, ${r.data.warnings?.length || 0} warnings). ` +
        `No questions modified.`
      );
      loadIsaManifests();
    } catch (err) {
      toast.error(`Upload failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsaUploading(false);
      e.target.value = '';
    }
  };

  // v6.32.58 — CR-02 WIDA Tag Coverage Audit (READ-ONLY).
  // Surfaces the WIDA-compliance risk that 0/1062 questions carry an
  // ELD Standard or Key Language Use tag. NEVER mutates question
  // records.
  const [wtaSummary, setWtaSummary] = useState(null);
  const [wtaUploading, setWtaUploading] = useState(false);
  const [wtaManifests, setWtaManifests] = useState([]);

  const loadWtaSummary = async () => {
    try {
      const r = await axios.get(`${API}/wida-tag-audit/summary`);
      setWtaSummary(r.data);
    } catch (e) {
      toast.error(`WIDA tag audit failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const loadWtaManifests = async () => {
    try {
      const r = await axios.get(`${API}/wida-tag-audit/manifests`);
      setWtaManifests(r.data?.manifests || []);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => {
    loadWtaSummary();
    loadWtaManifests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadWtaManifest = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Manifest must be a .csv file.');
      return;
    }
    if (!window.confirm(
      `Upload "${file.name}" as a WIDA tag manifest?\n\n` +
      `IMPORTANT: this is QUARANTINE only. The CSV will be parked for ` +
      `content-team review. NO question records will be modified by this upload. ` +
      `A separate apply step (not yet built) will perform the backfill ` +
      `after content-team sign-off.`
    )) {
      e.target.value = '';
      return;
    }
    setWtaUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API}/wida-tag-audit/upload-manifest`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(
        `Manifest stored (id=${r.data.manifest_id.slice(0, 8)}…, ` +
        `${r.data.row_count} rows, ${r.data.warnings?.length || 0} warnings). ` +
        `No questions modified.`
      );
      loadWtaManifests();
    } catch (err) {
      toast.error(`Upload failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setWtaUploading(false);
      e.target.value = '';
    }
  };

  // K-2 PDF filenames (3 grades × 4 modalities × 2 booklet types × 2 tiers = 48).
  // Generated server-side from the GridFS query — held here as a constant
  // so the click handler can ship without an extra round-trip.
  const K2_PDF_FILENAMES = useMemo(() => {
    const out = [];
    for (const g of ['K', '1', '2']) {
      for (const m of ['listening', 'reading', 'speaking', 'writing']) {
        for (const bt of ['student', 'teacher']) {
          for (const t of ['A', 'BC']) {
            out.push(`MOSAIC_${g}_${m}_${bt}_${t}.pdf`);
          }
        }
      }
    }
    return out;
  }, []);

  // Poll regen-press-status while a K-2 regen is running. The same status
  // endpoint covers any inline regen (cross-pod safe via MongoDB).
  useEffect(() => {
    if (!k2RegenBusy) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/content-auditor/regen-press-status`);
        const p = r.data?.progress || {};
        setK2RegenStatus(p);
        if (p.status === 'completed') {
          setK2RegenBusy(false);
          if ((p.fail || 0) === 0) {
            toast.success(`K-2 color regen complete — ${p.ok || 0} PDFs.`);
          } else {
            toast.warning(`K-2 regen complete with ${p.fail} failures (see details).`);
          }
        } else if (p.status === 'aborted' || p.status === 'failed') {
          setK2RegenBusy(false);
          toast.error(`K-2 regen aborted: ${p.aborted_reason || p.error || 'unknown'}`);
        }
      } catch (_) { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
  }, [k2RegenBusy]);

  const startK2ColorRegen = async () => {
    if (!window.confirm(
      `Regenerate all 48 K-2 BLM PDFs in COLOR now?\n\n` +
      `This will overwrite the cached grayscale PDFs in GridFS so school downloads ` +
      `pick up the v6.32.47 color rendering. ~8–12 minutes wall time. ` +
      `Grades 3+ are NOT touched.`
    )) return;
    setK2RegenBusy(true);
    setK2RegenStatus({ status: 'starting' });
    try {
      const r = await axios.post(`${API}/content-auditor/regen-press-pdfs`, {
        force_regen_all: true,
        batch_size: 1,
        inter_pdf_delay_sec: 2.0,
        filenames: K2_PDF_FILENAMES,
      });
      toast.info(`K-2 color regen started (${r.data?.queued || K2_PDF_FILENAMES.length} PDFs queued).`);
    } catch (e) {
      setK2RegenBusy(false);
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Could not start K-2 regen: ${msg}`);
    }
  };

  if (!stats) return <div className="py-16 text-center"><div className="loading-spinner mx-auto"></div></div>;

  return (
    <div className="space-y-6" data-testid="superadmin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard icon={Building2} value={stats.districts} label="Districts" onClick={() => onNavigate('districts')} />
        <StatCard icon={School} value={stats.schools} label="Schools" onClick={() => onNavigate('schools')} />
        <StatCard icon={Users} value={stats.users?.total} label="Total Users" onClick={() => onNavigate('users')} />
        <StatCard icon={Users} value={stats.users?.students} label="Students" accent="text-green-600" onClick={() => onNavigate('users', 'student')} />
        <StatCard icon={Users} value={stats.users?.teachers} label="Teachers" accent="text-blue-600" onClick={() => onNavigate('users', 'teacher')} />
        <StatCard icon={BarChart3} value={stats.assessments?.total} label="Assessments" accent="text-orange-600" onClick={() => onNavigate('audit')} />
      </div>

      {/* v6.32.43 — BVAF drift surfacing. Renders ONLY when there's drift. */}
      {stats.bvaf_drift && stats.bvaf_drift.drifted_count > 0 && (
        <div data-testid="bvaf-drift-banner" className="border-2 border-rose-200 bg-rose-50 p-4 rounded">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <Shield size={18} className="text-rose-600 mt-0.5" />
              <div>
                <div className="font-bold text-sm text-rose-900">
                  BVAF Image-Drift Detected — {stats.bvaf_drift.drifted_count} silent commit failure{stats.bvaf_drift.drifted_count === 1 ? '' : 's'}
                </div>
                <div className="text-xs text-rose-700 mt-1 max-w-2xl">
                  {stats.bvaf_drift.drifted_count} of {stats.bvaf_drift.active_committed} approved BVAF
                  scenes resolve to a missing live image file. Affected BLM PDFs, demo, and screener tests
                  will fall back to a generic image instead of the WIDA-Content-Fit approved one.
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="bvaf-drift-audit-btn"
                onClick={() => window.open('/api/bp-auditor/visual-fix/drift-audit', '_blank')}
                className="px-3 py-1.5 text-xs font-semibold border-2 border-rose-300 hover:bg-rose-100"
              >
                View Audit
              </button>
              <button
                data-testid="bvaf-drift-repair-btn"
                disabled={repairBusy}
                onClick={async () => {
                  if (!window.confirm(`Repair ${stats.bvaf_drift.drifted_count} drifted BVAF scenes from their candidate files?`)) return;
                  setRepairBusy(true);
                  setRepairResult(null);
                  try {
                    const r = await axios.post(`${API}/bp-auditor/visual-fix/backfill-drift`, {});
                    const t = r.data.totals || { repaired: 0, failed: 0 };
                    setRepairResult({
                      repaired: r.data.repaired || [],
                      failed: r.data.failed || [],
                      totals: t,
                    });
                    if (t.failed === 0) {
                      toast.success(`Repaired all ${t.repaired} scene${t.repaired === 1 ? '' : 's'}.`);
                    } else if (t.repaired > 0) {
                      toast.warning(`Repaired ${t.repaired}; ${t.failed} could not be recovered (see details below).`);
                    } else {
                      toast.error(`Could not repair any of the ${t.failed} drifted scene${t.failed === 1 ? '' : 's'} — see details below.`);
                    }
                    // v6.32.45 — refresh stats so the banner reflects the
                    // post-repair state (turns green when drifted_count
                    // hits 0). Without this, the banner stayed rose even
                    // after a successful prod repair.
                    if (onRefresh) await onRefresh();
                  } catch (e) {
                    toast.error(`Repair failed: ${e.response?.data?.detail || e.message}`);
                  } finally {
                    setRepairBusy(false);
                  }
                }}
                className="px-3 py-1.5 text-xs font-semibold bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {repairBusy ? 'Repairing…' : 'Repair Now'}
              </button>
            </div>
          </div>
        </div>
      )}
      {stats.bvaf_drift && stats.bvaf_drift.drifted_count === 0 && stats.bvaf_drift.active_committed > 0 && (
        <div data-testid="bvaf-drift-clean" className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-2 rounded inline-flex items-center gap-2">
          <Shield size={14} /> BVAF: all {stats.bvaf_drift.active_committed} committed scenes resolve correctly.
        </div>
      )}

      {/* v6.32.45 — Repair Now result detail. Shows operator exactly
          which scenes succeeded vs failed (with reason) so they don't
          have to curl the endpoint to debug a partial repair. */}
      {repairResult && (
        <div
          data-testid="bvaf-drift-repair-result"
          className={`border-2 p-4 rounded ${repairResult.totals.failed === 0 ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}
        >
          <div className="flex items-center justify-between mb-2">
            <div className="font-bold text-sm">
              Repair result — {repairResult.totals.repaired} repaired, {repairResult.totals.failed} failed
            </div>
            <button
              data-testid="bvaf-drift-repair-result-dismiss"
              onClick={() => setRepairResult(null)}
              className="text-xs text-gray-600 hover:text-gray-900 underline"
            >
              Dismiss
            </button>
          </div>
          {repairResult.totals.repaired > 0 && (
            <details className="text-xs mb-2">
              <summary className="cursor-pointer text-emerald-800 font-semibold">
                Repaired ({repairResult.totals.repaired})
              </summary>
              <ul className="mt-2 space-y-1 max-h-40 overflow-auto pl-2">
                {repairResult.repaired.slice(0, 50).map((r, i) => (
                  <li key={i} className="text-emerald-900">
                    Grade {r.grade} — {r.context}
                    <span className="text-emerald-700 ml-1">({r.candidate_resolution})</span>
                  </li>
                ))}
                {repairResult.repaired.length > 50 && (
                  <li className="text-emerald-700 italic">…and {repairResult.repaired.length - 50} more</li>
                )}
              </ul>
            </details>
          )}
          {repairResult.totals.failed > 0 && (
            <details open className="text-xs">
              <summary className="cursor-pointer text-amber-800 font-semibold">
                Could not recover ({repairResult.totals.failed})
              </summary>
              <ul className="mt-2 space-y-1 max-h-60 overflow-auto pl-2">
                {repairResult.failed.slice(0, 50).map((f, i) => (
                  <li key={i} className="text-amber-900">
                    <span className="font-semibold">{f.key}</span>
                    <span className="text-amber-700"> — {f.reason}</span>
                  </li>
                ))}
                {repairResult.failed.length > 50 && (
                  <li className="text-amber-700 italic">…and {repairResult.failed.length - 50} more</li>
                )}
              </ul>
              <div className="mt-2 text-amber-800 text-[11px]">
                Next step for unrecovered scenes: re-run BVAF (Big Pictures → Visual-Fix) for those
                grade/context pairs to regenerate fresh candidates, then click Repair Now again.
              </div>
            </details>
          )}
        </div>
      )}

      {/* v6.32.46 — BVAF Parity Audit panel: cryptographic proof that every
          BVAF approved scene's image renders byte-identically on BOTH the
          digital exam (SHA-256) AND the print BLM PDFs (perceptual hash on
          the grayscale-300dpi rendition). Read-only. */}
      <div data-testid="bvaf-parity-audit-panel" className="border-2 border-indigo-200 bg-indigo-50/40 p-4 rounded">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
          <div className="flex items-start gap-3">
            <Shield size={18} className="text-indigo-700 mt-0.5" />
            <div>
              <div className="font-bold text-sm text-indigo-900">
                BVAF Parity Audit — digital ↔ print byte-level proof
              </div>
              <div className="text-xs text-indigo-800 mt-1 max-w-2xl">
                Per-scene SHA-256 (digital) + perceptual-hash on the
                grayscale-300dpi rendition (print). PASS only when every
                question's resolved bytes equal the BVAF source AND at
                least one embedded PDF image matches the BVAF pHash within
                hamming distance ≤ 8.
              </div>
            </div>
          </div>
          <button
            data-testid="bvaf-parity-audit-run-btn"
            disabled={parityBusy}
            onClick={async () => {
              setParityBusy(true);
              setParityProgress(null);
              try {
                const r = await axios.post(`${API}/bp-auditor/visual-fix/parity-audit/start`, {});
                setParityReportId(r.data.report_id);
                toast.info(`Parity audit started: ${r.data.report_id}`);
              } catch (e) {
                setParityBusy(false);
                toast.error(`Could not start parity audit: ${e.response?.data?.detail || e.message}`);
              }
            }}
            className="px-3 py-1.5 text-xs font-semibold bg-indigo-700 text-white hover:bg-indigo-800 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {parityBusy ? 'Running…' : 'Run Parity Audit'}
          </button>
        </div>

        {parityBusy && parityProgress && (
          <div data-testid="bvaf-parity-audit-progress" className="text-xs text-indigo-800 mt-1">
            <span className="font-semibold">Phase:</span> {parityProgress.phase || '—'} •{' '}
            <span className="font-semibold">Digital:</span> {parityProgress.digital_progress || '—'} •{' '}
            <span className="font-semibold">PDF inventory:</span> {parityProgress.print_inventory_progress || '—'}
          </div>
        )}

        {parityReport && parityReport.totals && (
          <div data-testid="bvaf-parity-audit-result" className="mt-3 text-xs">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
              <div className={`border p-2 rounded ${parityReport.totals.full_parity_pass === parityReport.totals.scenes_with_healthy_source ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
                <div className="font-black text-base">{parityReport.totals.full_parity_pass}/{parityReport.totals.scenes_with_healthy_source}</div>
                <div className="text-[10px] text-gray-600">Full parity (digital+print)</div>
              </div>
              <div className="border border-gray-200 bg-white p-2 rounded">
                <div className="font-black text-base text-emerald-700">{parityReport.totals.digital_pass}</div>
                <div className="text-[10px] text-gray-600">Digital PASS ({parityReport.totals.digital_fail} fail)</div>
              </div>
              <div className="border border-gray-200 bg-white p-2 rounded">
                <div className="font-black text-base text-emerald-700">{parityReport.totals.print_pass}</div>
                <div className="text-[10px] text-gray-600">Print PASS ({parityReport.totals.print_fail} fail)</div>
              </div>
              <div className="border border-gray-200 bg-white p-2 rounded">
                <div className="font-black text-base text-gray-700">{parityReport.scenes?.length || 0}</div>
                <div className="text-[10px] text-gray-600">Scenes audited (started {parityReport.started_at?.slice(0, 16)})</div>
              </div>
            </div>

            {parityReport.totals.digital_fail > 0 && parityReport.scenes && (
              <details open className="border border-amber-200 bg-white rounded p-2">
                <summary className="cursor-pointer text-amber-800 font-semibold">
                  Digital parity failures ({parityReport.totals.digital_fail}) — questions whose bp_image_url does not resolve to BVAF bytes
                </summary>
                <ul className="mt-2 space-y-2 max-h-60 overflow-auto pl-2">
                  {parityReport.scenes
                    .filter(s => s.digital?.verdict === 'FAIL')
                    .map((s, i) => (
                      <li key={i} className="border-l-2 border-amber-400 pl-2">
                        <div className="font-semibold text-amber-900">
                          Grade {s.grade} — {s.context}{' '}
                          <span className="text-amber-700 font-normal">
                            ({s.digital.bad_count} of {s.digital.questions_count} questions)
                          </span>
                        </div>
                        <ul className="text-[11px] text-amber-800 mt-1">
                          {s.digital.evidence?.slice(0, 5).map((e, j) => (
                            <li key={j}>
                              q={e.q_id?.slice(0, 8)}… modality={e.modality} q#{e.q_num} —{' '}
                              <span className="font-mono">{e.reason}</span>
                              {e.q_sha256 && (
                                <span className="ml-1">
                                  (got {e.q_sha256.slice(0, 12)}…, expected {e.expected_sha256?.slice(0, 12)}…)
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                </ul>
              </details>
            )}

            {parityReport.totals.print_fail > 0 && parityReport.scenes && (
              <details className="border border-rose-200 bg-white rounded p-2 mt-2">
                <summary className="cursor-pointer text-rose-800 font-semibold">
                  Print parity failures ({parityReport.totals.print_fail}) — BVAF pHash not found in any grade-matching PDF
                </summary>
                <ul className="mt-2 space-y-2 max-h-60 overflow-auto pl-2">
                  {parityReport.scenes
                    .filter(s => s.print?.verdict === 'FAIL')
                    .map((s, i) => (
                      <li key={i} className="border-l-2 border-rose-400 pl-2">
                        <div className="font-semibold text-rose-900">Grade {s.grade} — {s.context}</div>
                        <div className="text-[11px] text-rose-700">
                          BVAF pHash: <span className="font-mono">{s.print.bvaf_phash_gray300}</span>
                        </div>
                        <ul className="text-[11px] text-rose-700 mt-1">
                          {s.print.closest_mismatches?.slice(0, 3).map((c, j) => (
                            <li key={j}>
                              closest in {c.pdf} p{c.page} — distance {c.distance}
                            </li>
                          ))}
                        </ul>
                      </li>
                    ))}
                </ul>
              </details>
            )}

            <details className="border border-emerald-200 bg-white rounded p-2 mt-2">
              <summary className="cursor-pointer text-emerald-800 font-semibold">
                Sample passing evidence ({parityReport.totals.full_parity_pass} scenes — click to see SHA-256 + pHash distance proofs)
              </summary>
              <ul className="mt-2 space-y-2 max-h-60 overflow-auto pl-2 text-[11px]">
                {parityReport.scenes
                  ?.filter(s => s.digital?.verdict === 'PASS' && s.print?.verdict === 'PASS')
                  .slice(0, 10)
                  .map((s, i) => (
                    <li key={i} className="border-l-2 border-emerald-400 pl-2">
                      <div className="font-semibold text-emerald-900">Grade {s.grade} — {s.context}</div>
                      <div className="text-emerald-700">
                        digital sha256: <span className="font-mono">{s.digital.verified_sha256?.slice(0, 24)}…</span>{' '}
                        ({s.digital.questions_count} questions verified)
                      </div>
                      <div className="text-emerald-700">
                        print pHash {s.print.bvaf_phash_gray300} matched in{' '}
                        {s.print.match_count} embedded image{s.print.match_count === 1 ? '' : 's'} (best: {s.print.best_matches?.[0]?.pdf} p{s.print.best_matches?.[0]?.page} d={s.print.best_matches?.[0]?.distance})
                      </div>
                    </li>
                  ))}
              </ul>
            </details>
          </div>
        )}

        {!parityReport && !parityBusy && (
          <div className="text-xs text-indigo-700 mt-2 italic">
            No parity report yet. Click [Run Parity Audit] to generate cryptographic proof.
          </div>
        )}
      </div>

      {/* v6.32.48 — K-2 Distractor Pictograph Coverage. Surfaces silent
          visual-support gaps on print BLM answer cards before a teacher
          spots them. Auto-loads on mount so the dashboard always shows
          the current coverage rate. */}
      <div data-testid="picto-coverage-panel" className={`border-2 p-4 rounded ${pictoCov && pictoCov.totals.coverage_pct >= 95 ? 'border-emerald-200 bg-emerald-50/40' : pictoCov && pictoCov.totals.coverage_pct >= 70 ? 'border-amber-200 bg-amber-50/40' : 'border-rose-200 bg-rose-50/40'}`}>
        <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
          <div className="flex items-start gap-3">
            <Shield size={18} className={`${pictoCov && pictoCov.totals.coverage_pct >= 95 ? 'text-emerald-700' : pictoCov && pictoCov.totals.coverage_pct >= 70 ? 'text-amber-700' : 'text-rose-700'} mt-0.5`} />
            <div>
              <div className="font-bold text-sm">
                K-2 Distractor Pictograph Coverage
              </div>
              <div className="text-xs text-gray-700 mt-1 max-w-2xl">
                Every K-2 answer choice on the print BLM uses a 4-row card
                (letter → pictogram → text → bubble). When no pictogram is
                mapped for an option, pre-readers see a text-only box —
                breaking WIDA visual-support convention. This panel scans
                all K-2 listening / reading question options and flags
                silent gaps before a teacher reports them.
              </div>
            </div>
          </div>
          <button
            data-testid="picto-coverage-refresh-btn"
            disabled={pictoBusy}
            onClick={loadPictoCoverage}
            className="px-3 py-1.5 text-xs font-semibold bg-gray-700 text-white hover:bg-gray-900 disabled:opacity-60"
          >
            {pictoBusy ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {pictoCov && (
          <div data-testid="picto-coverage-result" className="mt-3 text-xs">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
              <div className="border border-gray-200 bg-white p-2 rounded">
                <div className="font-black text-base">{pictoCov.totals.coverage_pct}%</div>
                <div className="text-[10px] text-gray-600">Coverage</div>
              </div>
              <div className="border border-gray-200 bg-white p-2 rounded">
                <div className="font-black text-base text-emerald-700">{pictoCov.totals.mapped_total}</div>
                <div className="text-[10px] text-gray-600">Mapped (of {pictoCov.totals.total_option_occurrences})</div>
              </div>
              <div className="border border-gray-200 bg-white p-2 rounded">
                <div className="font-black text-base text-amber-700">{pictoCov.totals.unmapped}</div>
                <div className="text-[10px] text-gray-600">Unmapped occurrences</div>
              </div>
              <div className="border border-gray-200 bg-white p-2 rounded">
                <div className="font-black text-base text-amber-700">{pictoCov.totals.distinct_unmapped_strings}</div>
                <div className="text-[10px] text-gray-600">Distinct strings</div>
              </div>
              <div className="border border-gray-200 bg-white p-2 rounded">
                <div className="font-black text-base">{pictoCov.totals.affected_questions_count}</div>
                <div className="text-[10px] text-gray-600">Affected questions (of {pictoCov.totals.k2_questions_with_options})</div>
              </div>
            </div>

            <div className="text-[11px] text-gray-700 mb-2">
              Mapped via exact dictionary: <span className="font-semibold">{pictoCov.totals.mapped_exact}</span> ·
              via keyword fallback: <span className="font-semibold">{pictoCov.totals.mapped_keyword_fallback}</span>
            </div>

            {pictoCov.top_unmapped && pictoCov.top_unmapped.length > 0 && (
              <details open className="border border-amber-200 bg-white rounded p-2">
                <summary className="cursor-pointer text-amber-800 font-semibold">
                  Top {pictoCov.top_unmapped.length} unmapped distractors (text-only on print)
                </summary>
                <div tabIndex={0} role="region" aria-label="Top unmapped distractors" className="max-h-60 overflow-auto focus:outline focus:outline-2 focus:outline-blue-500">
                  <ul className="mt-2 space-y-1 pl-2 text-[11px]">
                  {pictoCov.top_unmapped.map((u, i) => (
                    <li key={i} className="text-amber-900">
                      <span className="font-mono text-amber-700 mr-2">{u.occurrences}×</span>
                      <span className="font-semibold">{u.option_text}</span>
                    </li>
                  ))}
                </ul>
                </div>
                <div className="mt-2 text-[11px] text-gray-700 italic">
                  {pictoCov.next_action}
                </div>
              </details>
            )}

            {pictoCov.totals.unmapped === 0 && (
              <div data-testid="picto-coverage-clean" className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
                100% of K-2 distractors have visual support. No further action needed.
              </div>
            )}
          </div>
        )}
      </div>

      {/* v6.32.49 — One-click K-2 BLM color regen. After deploying v6.32.47,
          GridFS still holds the old grayscale PDFs; teachers download the
          stale cached versions. This panel fires the press-pdf regen for
          just the 48 K-2 filenames, in batch_size=1 with disk + memory
          guards from v6.32.42. Grades 3+ untouched. */}
      <div data-testid="k2-color-regen-panel" className="border-2 border-orange-200 bg-orange-50/40 p-4 rounded">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
          <div className="flex items-start gap-3">
            <Printer size={18} className="text-orange-700 mt-0.5" />
            <div>
              <div className="font-bold text-sm text-orange-900">
                K-2 Color Print Regen
              </div>
              <div className="text-xs text-orange-800 mt-1 max-w-2xl">
                Regenerates the cached K, 1, 2 BLM PDFs in GridFS so teacher
                downloads pick up v6.32.47's WIDA-aligned color rendering and
                v6.32.48's pictograph keyword fallback. Runs on 48 PDFs in
                batch_size=1 with disk + memory guards (~8–12 min). Grades 3+
                are NOT touched. Required ONCE after each color/pictograph
                code update.
              </div>
            </div>
          </div>
          <button
            data-testid="k2-color-regen-btn"
            disabled={k2RegenBusy}
            onClick={startK2ColorRegen}
            className="px-3 py-1.5 text-xs font-semibold bg-orange-700 text-white hover:bg-orange-800 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {k2RegenBusy ? 'Regenerating…' : 'Regenerate K-2 in Color'}
          </button>
        </div>

        {k2RegenStatus && (
          <div data-testid="k2-color-regen-status" className="mt-3 text-xs">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-semibold text-orange-900">Status:</span>
              <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                k2RegenStatus.status === 'completed' ? 'bg-emerald-100 text-emerald-800' :
                k2RegenStatus.status === 'running' || k2RegenStatus.status === 'starting' ? 'bg-amber-100 text-amber-800' :
                'bg-rose-100 text-rose-800'
              }`}>
                {k2RegenStatus.status || '—'}
              </span>
              {typeof k2RegenStatus.done === 'number' && (
                <span className="text-orange-800">
                  Progress: <span className="font-mono">{k2RegenStatus.done}/{k2RegenStatus.total || K2_PDF_FILENAMES.length}</span>
                  {' '}— ok={k2RegenStatus.ok || 0} fail={k2RegenStatus.fail || 0} skip={k2RegenStatus.skip || 0}
                </span>
              )}
              {k2RegenStatus.current && (
                <span className="text-orange-700 font-mono text-[11px] truncate max-w-xs">
                  current: {k2RegenStatus.current}
                </span>
              )}
            </div>
            {typeof k2RegenStatus.done === 'number' && (k2RegenStatus.total || 0) > 0 && (
              <div className="mt-2 w-full bg-orange-100 rounded h-2 overflow-hidden">
                <div
                  className="bg-orange-600 h-full transition-all"
                  style={{ width: `${Math.min(100, (k2RegenStatus.done / k2RegenStatus.total) * 100)}%` }}
                />
              </div>
            )}
            {k2RegenStatus.aborted_reason && (
              <div className="mt-2 text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                Aborted: <span className="font-mono">{k2RegenStatus.aborted_reason}</span>
              </div>
            )}
            {k2RegenStatus.errors && k2RegenStatus.errors.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-rose-700 font-semibold">
                  Errors ({k2RegenStatus.errors.length})
                </summary>
                <ul className="mt-1 pl-3 space-y-1 text-[11px] max-h-40 overflow-auto">
                  {k2RegenStatus.errors.slice(0, 20).map((e, i) => (
                    <li key={i} className="text-rose-800 font-mono">{typeof e === 'string' ? e : JSON.stringify(e)}</li>
                  ))}
                </ul>
              </details>
            )}
            {k2RegenStatus.status === 'completed' && (k2RegenStatus.fail || 0) === 0 && (
              <div className="mt-2 text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
                Done. The K-2 PDFs in GridFS are now color. Teachers downloading
                from this point on will get the new version. To verify, download
                <span className="font-mono"> MOSAIC_K_listening_student_A.pdf </span>
                and check page 9 (Lunar New Year) — expect red lanterns + gold
                tones + pictographs on all 4 answer boxes.
              </div>
            )}
          </div>
        )}
      </div>

      {/* v6.32.53 — Print-Shop Package downloader. Distinct from the
          per-school digital-license download. Lets the Super Admin
          fetch a clean publisher-style PDF for the resale SKU (no
          diagonal "Licensed to..." stamp) or a red SAMPLE diagonal
          for sales decks / procurement reviews. */}
      <div data-testid="print-shop-panel" className="border-2 border-blue-200 bg-blue-50/40 p-4 rounded">
        <div className="flex items-start gap-3 mb-3">
          <Printer size={18} className="text-blue-700 mt-0.5" />
          <div>
            <div className="font-bold text-sm text-blue-900">
              Print-Shop Package Downloader
            </div>
            <div className="text-xs text-blue-800 mt-1 max-w-3xl">
              Download a BLM PDF with the right watermark policy for its
              destination. <span className="font-semibold">Print Resale</span> = clean
              publisher-style footer (no diagonal stamp) for the physical
              resale SKU. <span className="font-semibold">Sample</span> = big red
              SAMPLE diagonal for sales decks or procurement reviews.
              <span className="font-semibold"> Digital License</span> = legacy
              "Licensed to {'{school}'}" diagonal (audit-debugging only —
              the regular per-school /download endpoint already applies this).
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-[11px] font-semibold text-blue-900 mb-1">BLM filename</label>
            <select
              data-testid="print-shop-filename"
              aria-label="BLM filename"
              value={printShopSelected}
              onChange={(e) => setPrintShopSelected(e.target.value)}
              className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
            >
              {printShopFiles.length === 0 && <option value="">Loading…</option>}
              {printShopFiles.map(f => (
                <option key={f.filename} value={f.filename}>
                  {f.filename} ({f.size_mb} MB)
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-blue-900 mb-1">Watermark policy</label>
            <select
              data-testid="print-shop-mode"
              aria-label="Watermark policy"
              value={printShopMode}
              onChange={(e) => setPrintShopMode(e.target.value)}
              className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
            >
              <option value="print_resale">Print Resale — clean footer, no diagonal</option>
              <option value="sample">Sample — red SAMPLE diagonal (NOT FOR ASSESSMENT USE)</option>
              <option value="digital_license">Digital License — legacy diagonal "Licensed to..."</option>
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-blue-900 mb-1">
              School name <span className="text-blue-600 font-normal">(footer / Licensed-to)</span>
            </label>
            <input
              data-testid="print-shop-school"
              type="text"
              value={printShopSchool}
              onChange={(e) => setPrintShopSchool(e.target.value)}
              placeholder="e.g. PS 120 NYC"
              className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-blue-900 mb-1">
              Order number <span className="text-blue-600 font-normal">(footer)</span>
            </label>
            <input
              data-testid="print-shop-order"
              type="text"
              value={printShopOrder}
              onChange={(e) => setPrintShopOrder(e.target.value)}
              placeholder="e.g. PO-2026-0042"
              className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            data-testid="print-shop-download-btn"
            disabled={printShopBusy || !printShopSelected}
            onClick={downloadPrintShopPackage}
            className="px-3 py-1.5 text-xs font-semibold bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {printShopBusy ? (
              <><Loader2 size={12} className="animate-spin" /> Building…</>
            ) : (
              <><Printer size={12} /> Download {printShopMode === 'print_resale' ? 'Print Resale' : printShopMode === 'sample' ? 'Sample' : 'Licensed'} PDF</>
            )}
          </button>
        </div>
      </div>

      {/* v6.32.54 — Password-Protected Master Print Archive. Bundles
          every K-12 BLM PDF in GridFS into a single AES-256-encrypted
          ZIP that the operator can email or save-to-disk for a print
          shop. Each PDF is stamped with the print_resale watermark
          (clean publisher footer; no diagonal Licensed-to). */}
      <div data-testid="print-master-archive-panel" className="border-2 border-emerald-200 bg-emerald-50/40 p-4 rounded">
        <div className="flex items-start gap-3 mb-3">
          <Lock size={18} className="text-emerald-700 mt-0.5" />
          <div>
            <div className="font-bold text-sm text-emerald-900">
              Password-Protected Master Print Archive
            </div>
            <div className="text-xs text-emerald-800 mt-1 max-w-3xl">
              Bundle <span className="font-semibold">every K-12 BLM PDF</span> (112 files,
              all grades × all 4 modalities × student/teacher × tier A/BC) into
              a single <span className="font-semibold">AES-256-encrypted ZIP</span> with
              the print_resale watermark. Email or save-to-disk one file to your
              print shop instead of managing 112 individual downloads. The
              password you set below encrypts the archive and is{' '}
              <span className="font-semibold">never stored on the server</span> —
              copy it somewhere safe before clicking Build.
            </div>
          </div>
        </div>

        {/* Build form (hidden once a job is running or completed unless operator wants to rebuild) */}
        {(!pmaJob || pmaJob.status === 'failed' || pmaJob.status === 'completed') && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-[11px] font-semibold text-emerald-900 mb-1">
                Archive password <span className="text-emerald-700 font-normal">(8+ chars, AES-256)</span>
              </label>
              <input
                data-testid="pma-password"
                type="password"
                value={pmaPassword}
                onChange={(e) => setPmaPassword(e.target.value)}
                placeholder="Set archive password"
                autoComplete="new-password"
                className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-emerald-900 mb-1">
                Confirm password
              </label>
              <input
                data-testid="pma-password-confirm"
                type="password"
                value={pmaConfirm}
                onChange={(e) => setPmaConfirm(e.target.value)}
                placeholder="Re-type password"
                autoComplete="new-password"
                className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-emerald-900 mb-1">
                Edition <span className="text-emerald-700 font-normal">(footer)</span>
              </label>
              <input
                data-testid="pma-edition"
                type="text"
                value={pmaEdition}
                onChange={(e) => setPmaEdition(e.target.value)}
                placeholder="e.g. 2026.1"
                className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-emerald-900 mb-1">
                Order number <span className="text-emerald-700 font-normal">(optional footer)</span>
              </label>
              <input
                data-testid="pma-order"
                type="text"
                value={pmaOrder}
                onChange={(e) => setPmaOrder(e.target.value)}
                placeholder="e.g. PO-2026-0042"
                className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
              />
            </div>
          </div>
        )}

        {/* Live progress while running */}
        {pmaJob && pmaJob.status === 'running' && (
          <div data-testid="pma-progress" className="border border-emerald-300 bg-white rounded p-3 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <Loader2 size={14} className="text-emerald-700 animate-spin" />
              <span className="text-xs font-semibold text-emerald-900">
                Building master archive…
              </span>
              <span className="text-[10px] text-emerald-700 ml-auto uppercase tracking-wider">
                phase: {pmaJob.phase || '—'}
              </span>
            </div>
            <div className="h-1.5 bg-emerald-100 rounded overflow-hidden mb-2">
              <div
                className="h-full bg-emerald-600 transition-all"
                style={{
                  width: pmaJob.total_pdfs
                    ? `${Math.round(100 * (pmaJob.processed || 0) / pmaJob.total_pdfs)}%`
                    : '0%',
                }}
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] text-emerald-900">
              <div><span className="text-emerald-600">processed:</span> {pmaJob.processed || 0} / {pmaJob.total_pdfs || '?'}</div>
              <div><span className="text-emerald-600">ok:</span> {pmaJob.ok || 0}</div>
              <div><span className="text-emerald-600">fail:</span> {pmaJob.fail || 0}</div>
              <div><span className="text-emerald-600">elapsed:</span> {pmaJob.elapsed_sec || 0}s</div>
            </div>
            {pmaJob.current && (
              <div className="text-[10px] text-emerald-700 mt-1 truncate">
                current: {pmaJob.current}
              </div>
            )}
          </div>
        )}

        {/* Completed state — download button + summary */}
        {pmaJob && pmaJob.status === 'completed' && (
          <div data-testid="pma-completed" className="border border-emerald-400 bg-white rounded p-3 mb-3">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={14} className="text-emerald-700" />
              <span className="text-xs font-semibold text-emerald-900">
                Archive ready: {pmaJob.archive_filename}
              </span>
              <span className="text-[10px] text-emerald-700 ml-auto">
                {pmaJob.archive_size_mb} MB · {pmaJob.ok}/{pmaJob.total_pdfs} PDFs · AES-256
              </span>
            </div>
            {pmaJob.fail > 0 && (
              <div className="text-[11px] text-amber-800 mb-2">
                {pmaJob.fail} PDF(s) failed to bundle. Re-run if needed.
                {Array.isArray(pmaJob.warnings) && pmaJob.warnings.length > 0 && (
                  <ul className="mt-1 ml-4 list-disc">
                    {pmaJob.warnings.slice(0, 5).map((w, i) => (
                      <li key={i} className="truncate">{w}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <button
              data-testid="pma-download-btn"
              onClick={downloadMasterArchive}
              className="px-3 py-1.5 text-xs font-semibold bg-emerald-700 text-white hover:bg-emerald-800 inline-flex items-center gap-2"
            >
              <Download size={12} /> Download encrypted ZIP
            </button>
          </div>
        )}

        {/* Failed state */}
        {pmaJob && pmaJob.status === 'failed' && (
          <div data-testid="pma-failed" className="border border-rose-300 bg-rose-50 rounded p-3 mb-3 text-[11px] text-rose-900">
            <div className="font-semibold mb-1">Build failed</div>
            <div className="font-mono text-[10px] whitespace-pre-wrap">
              {pmaJob.error || '(no error message)'}
            </div>
          </div>
        )}

        {/* Build button — visible when no run is in flight */}
        {(!pmaJob || pmaJob.status === 'failed' || pmaJob.status === 'completed') && (
          <div className="flex justify-end">
            <button
              data-testid="pma-build-btn"
              disabled={pmaStarting || pmaPassword.length < 8 || pmaPassword !== pmaConfirm}
              onClick={startMasterArchive}
              className="px-3 py-1.5 text-xs font-semibold bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              {pmaStarting ? (
                <><Loader2 size={12} className="animate-spin" /> Starting build…</>
              ) : (
                <><Lock size={12} /> {pmaJob && pmaJob.status === 'completed' ? 'Rebuild Master Archive' : 'Build Master Archive'}</>
              )}
            </button>
          </div>
        )}
      </div>

      {/* v6.32.57 — CR-01 Image Source Audit panel. READ-ONLY visibility
          tooling that surfaces the WIDA-alignment risk that 70% of the
          question bank serves Unsplash CDN URLs instead of content-team-
          vetted /bp_images/ assets. Manifest upload is QUARANTINE-ONLY
          (parks the CSV for content-team review; never auto-applies). */}
      <div data-testid="image-source-audit-panel" className="border-2 border-amber-200 bg-amber-50/40 p-4 rounded">
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle size={18} className="text-amber-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-sm text-amber-900">
              Image Source Audit — WIDA alignment visibility
            </div>
            <div className="text-xs text-amber-800 mt-1 max-w-3xl">
              Read-only audit of every question's image source. Listening
              questions use locally-stored, content-team-vetted{' '}
              <span className="font-mono">/bp_images/</span> assets ("BVAF").
              Reading / speaking / writing questions historically fell back to{' '}
              <span className="font-mono">unsplash.com</span> CDN URLs that have
              had no content-team review. This panel surfaces the gap so
              content team can plan a vetted-asset backfill.{' '}
              <span className="font-semibold">Nothing on this panel mutates
              question records.</span>
            </div>
          </div>
          <button
            data-testid="isa-refresh-btn"
            onClick={loadIsaSummary}
            disabled={isaLoading}
            className="text-[11px] px-2 py-1 border border-amber-700 text-amber-900 hover:bg-amber-100 disabled:opacity-50 inline-flex items-center gap-1"
          >
            <RefreshCw size={11} className={isaLoading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        {isaSummary && (
          <>
            {/* Totals strip */}
            <div data-testid="isa-totals" className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
              <div className="border border-emerald-200 bg-emerald-50 p-2">
                <div className="text-[10px] text-emerald-700 uppercase tracking-wider">BVAF (vetted)</div>
                <div className="text-xl font-bold text-emerald-900">{isaSummary.totals.bvaf}</div>
                <div className="text-[10px] text-emerald-700">
                  {isaSummary.totals.total ? Math.round(100 * isaSummary.totals.bvaf / isaSummary.totals.total) : 0}%
                </div>
              </div>
              <div className="border border-rose-300 bg-rose-50 p-2">
                <div className="text-[10px] text-rose-700 uppercase tracking-wider">Unsplash (RISK)</div>
                <div className="text-xl font-bold text-rose-900">{isaSummary.totals.unsplash}</div>
                <div className="text-[10px] text-rose-700">
                  {isaSummary.totals.total ? Math.round(100 * isaSummary.totals.unsplash / isaSummary.totals.total) : 0}%
                </div>
              </div>
              <div className="border border-amber-200 bg-amber-50 p-2">
                <div className="text-[10px] text-amber-700 uppercase tracking-wider">External (other)</div>
                <div className="text-xl font-bold text-amber-900">{isaSummary.totals.external}</div>
              </div>
              <div className="border border-gray-300 bg-gray-50 p-2">
                <div className="text-[10px] text-gray-600 uppercase tracking-wider">Missing</div>
                <div className="text-xl font-bold text-gray-900">{isaSummary.totals.missing}</div>
              </div>
              <div className="border-2 border-gray-700 bg-white p-2">
                <div className="text-[10px] text-gray-600 uppercase tracking-wider">Total</div>
                <div className="text-xl font-bold">{isaSummary.totals.total}</div>
              </div>
            </div>

            {/* Grade × modality breakdown */}
            <div className="border border-amber-200 bg-white rounded mb-4 overflow-x-auto">
              <table data-testid="isa-grade-modality-table" className="w-full text-[11px]">
                <thead className="bg-amber-100">
                  <tr>
                    <th className="text-left px-2 py-1.5">Grade</th>
                    <th className="text-left px-2 py-1.5">Modality</th>
                    <th className="text-right px-2 py-1.5 text-emerald-700">BVAF</th>
                    <th className="text-right px-2 py-1.5 text-rose-700">Unsplash</th>
                    <th className="text-right px-2 py-1.5 text-amber-700">External</th>
                    <th className="text-right px-2 py-1.5 text-gray-600">Missing</th>
                    <th className="text-right px-2 py-1.5">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(isaSummary.by_grade_modality || {})
                    .sort(([a], [b]) => a.localeCompare(b))
                    .flatMap(([grade, mods]) =>
                      Object.entries(mods).map(([modality, counts]) => (
                        <tr key={`${grade}-${modality}`} className="border-t border-amber-100">
                          <td className="px-2 py-1 font-semibold">{grade}</td>
                          <td className="px-2 py-1">{modality}</td>
                          <td className="px-2 py-1 text-right text-emerald-700">{counts.bvaf}</td>
                          <td className={`px-2 py-1 text-right ${counts.unsplash > 0 ? 'text-rose-700 font-bold' : 'text-rose-300'}`}>{counts.unsplash}</td>
                          <td className="px-2 py-1 text-right text-amber-700">{counts.external}</td>
                          <td className="px-2 py-1 text-right text-gray-600">{counts.missing}</td>
                          <td className="px-2 py-1 text-right font-semibold">{counts.total}</td>
                        </tr>
                      ))
                    )
                  }
                </tbody>
              </table>
            </div>

            {/* Drill-in: pick a classification + filters, view actual question rows */}
            <div className="border border-amber-200 bg-white rounded p-3 mb-4">
              <div className="flex flex-wrap items-end gap-2 mb-2">
                <div>
                  <label className="block text-[10px] font-semibold text-amber-900 mb-0.5">Classification</label>
                  <select
                    data-testid="isa-filter-class"
                    aria-label="Image source classification filter"
                    value={isaFilterClass}
                    onChange={(e) => setIsaFilterClass(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                  >
                    <option value="unsplash">unsplash (risk)</option>
                    <option value="external">external (other)</option>
                    <option value="missing">missing</option>
                    <option value="bvaf">bvaf (vetted)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-amber-900 mb-0.5">Grade (optional)</label>
                  <input
                    data-testid="isa-filter-grade"
                    type="text"
                    value={isaFilterGrade}
                    onChange={(e) => setIsaFilterGrade(e.target.value)}
                    placeholder="K, 1, 2, 3-4, ..."
                    className="text-xs border border-gray-300 rounded px-2 py-1 bg-white w-28"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-amber-900 mb-0.5">Modality (optional)</label>
                  <select
                    data-testid="isa-filter-modality"
                    aria-label="Image source modality filter (optional)"
                    value={isaFilterModality}
                    onChange={(e) => setIsaFilterModality(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
                  >
                    <option value="">all</option>
                    <option value="listening">listening</option>
                    <option value="reading">reading</option>
                    <option value="speaking">speaking</option>
                    <option value="writing">writing</option>
                  </select>
                </div>
                <button
                  data-testid="isa-load-questions-btn"
                  onClick={loadIsaQuestions}
                  className="text-xs px-3 py-1 bg-amber-700 text-white hover:bg-amber-800"
                >
                  Load matching questions
                </button>
                {isaQuestionsTotal > 0 && (
                  <span className="text-[11px] text-amber-700 ml-auto">
                    showing {isaQuestions.length} of {isaQuestionsTotal} matches
                  </span>
                )}
              </div>

              {isaQuestions.length > 0 && (
                <div data-testid="isa-questions-table" className="border border-amber-100 rounded overflow-x-auto max-h-72 overflow-y-auto">
                  <table className="w-full text-[10px]">
                    <thead className="bg-amber-50 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1">Q ID</th>
                        <th className="text-left px-2 py-1">Grade</th>
                        <th className="text-left px-2 py-1">Modality</th>
                        <th className="text-left px-2 py-1">Class</th>
                        <th className="text-left px-2 py-1">bp_image_url</th>
                        <th className="text-left px-2 py-1">graphic_support_url</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isaQuestions.map((q) => (
                        <tr key={q.question_id} className="border-t border-amber-50 hover:bg-amber-50/40">
                          <td className="px-2 py-0.5 font-mono">{q.question_id?.slice(0, 8)}…</td>
                          <td className="px-2 py-0.5">{q.grade}</td>
                          <td className="px-2 py-0.5">{q.modality}</td>
                          <td className={`px-2 py-0.5 font-semibold ${q.classification === 'unsplash' ? 'text-rose-700' : q.classification === 'bvaf' ? 'text-emerald-700' : 'text-amber-700'}`}>
                            {q.classification}
                          </td>
                          <td className="px-2 py-0.5 font-mono text-[9px] truncate max-w-[160px]" title={q.bp_image_url || ''}>
                            {q.bp_image_url || <span className="text-gray-400">null</span>}
                          </td>
                          <td className="px-2 py-0.5 font-mono text-[9px] truncate max-w-[280px]" title={q.graphic_support_url || ''}>
                            {q.graphic_support_url || <span className="text-gray-400">null</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Manifest upload (quarantine-only) */}
            <div className="border border-amber-300 bg-white rounded p-3">
              <div className="text-xs font-bold text-amber-900 mb-1 inline-flex items-center gap-2">
                <Upload size={12} /> BVAF replacement manifest (quarantine upload)
              </div>
              <div className="text-[11px] text-amber-800 mb-2">
                Upload a content-team-approved CSV with columns{' '}
                <span className="font-mono">question_id, new_bp_image_url, new_graphic_support_url, reviewer, reviewed_at, notes</span>.{' '}
                The file is parked in <span className="font-mono">bvaf_replacement_manifest_pending</span> for review.{' '}
                <span className="font-semibold">No question records are modified by this upload.</span>{' '}
                A future apply step (gated behind operator confirmation) will perform the backfill.
              </div>
              <div className="flex items-center gap-2">
                <input
                  data-testid="isa-manifest-upload"
                  aria-label="Image source manifest CSV upload"
                  type="file"
                  accept=".csv"
                  onChange={uploadIsaManifest}
                  disabled={isaUploading}
                  className="text-[11px]"
                />
                {isaUploading && (
                  <span className="text-[11px] text-amber-700 inline-flex items-center gap-1">
                    <Loader2 size={11} className="animate-spin" /> Validating + storing…
                  </span>
                )}
              </div>

              {isaManifests.length > 0 && (
                <div className="mt-3 border-t border-amber-100 pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1">
                    Pending manifests ({isaManifests.length})
                  </div>
                  <div className="space-y-1">
                    {isaManifests.slice(0, 5).map((m) => (
                      <div key={m.manifest_id} data-testid={`isa-manifest-${m.manifest_id}`} className="text-[10px] flex items-center gap-2 font-mono">
                        <span className="text-amber-700">{m.uploaded_at?.slice(0, 19)}</span>
                        <span className="font-semibold">{m.filename}</span>
                        <span className="text-amber-700">{m.row_count} rows</span>
                        {m.warnings && m.warnings.length > 0 && (
                          <span className="text-rose-700">{m.warnings.length} warnings</span>
                        )}
                        <span className="ml-auto text-amber-700">{m.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* v6.32.58 — CR-02 WIDA Tag Coverage Audit panel. READ-ONLY visibility
          tooling that surfaces the compliance risk: 0/1062 questions carry
          ELD Standard or Key Language Use tags. Manifest upload is QUARANTINE
          ONLY (parks CSV for content-team review; never auto-applies). */}
      <div data-testid="wida-tag-audit-panel" className="border-2 border-violet-200 bg-violet-50/40 p-4 rounded">
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle size={18} className="text-violet-700 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-sm text-violet-900">
              WIDA Tag Coverage Audit — ELD Standard + Key Language Use
            </div>
            <div className="text-xs text-violet-800 mt-1 max-w-3xl">
              The WIDA rubric requires every question to carry an{' '}
              <span className="font-mono">eld_standard</span> tag (1-5: Social/Instructional,
              Language Arts, Math, Science, Social Studies) and a{' '}
              <span className="font-mono">key_language_use</span> tag (Narrate / Inform / Explain / Argue) so proficiency rollup
              and standards-aligned reporting can compute correctly. This panel surfaces
              coverage gaps so content team can plan a tag backfill.{' '}
              <span className="font-semibold">Nothing on this panel mutates question records.</span>
            </div>
          </div>
          <button
            data-testid="wta-refresh-btn"
            onClick={loadWtaSummary}
            className="text-[11px] px-2 py-1 border border-violet-700 text-violet-900 hover:bg-violet-100 inline-flex items-center gap-1"
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>

        {wtaSummary && (
          <>
            <div data-testid="wta-totals" className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
              <div className="border border-rose-300 bg-rose-50 p-2">
                <div className="text-[10px] text-rose-700 uppercase tracking-wider">Neither tag</div>
                <div className="text-xl font-bold text-rose-900">{wtaSummary.totals.neither}</div>
                <div className="text-[10px] text-rose-700">
                  {wtaSummary.totals.total ? Math.round(100 * wtaSummary.totals.neither / wtaSummary.totals.total) : 0}%
                </div>
              </div>
              <div className="border border-amber-200 bg-amber-50 p-2">
                <div className="text-[10px] text-amber-700 uppercase tracking-wider">With ELD only</div>
                <div className="text-xl font-bold text-amber-900">{wtaSummary.totals.with_eld - wtaSummary.totals.with_both}</div>
              </div>
              <div className="border border-amber-200 bg-amber-50 p-2">
                <div className="text-[10px] text-amber-700 uppercase tracking-wider">With KLU only</div>
                <div className="text-xl font-bold text-amber-900">{wtaSummary.totals.with_klu - wtaSummary.totals.with_both}</div>
              </div>
              <div className="border border-emerald-200 bg-emerald-50 p-2">
                <div className="text-[10px] text-emerald-700 uppercase tracking-wider">Both (compliant)</div>
                <div className="text-xl font-bold text-emerald-900">{wtaSummary.totals.with_both}</div>
                <div className="text-[10px] text-emerald-700">
                  {wtaSummary.totals.total ? Math.round(100 * wtaSummary.totals.with_both / wtaSummary.totals.total) : 0}%
                </div>
              </div>
              <div className="border-2 border-gray-700 bg-white p-2">
                <div className="text-[10px] text-gray-600 uppercase tracking-wider">Total</div>
                <div className="text-xl font-bold">{wtaSummary.totals.total}</div>
              </div>
            </div>

            <div className="border border-violet-200 bg-white rounded mb-4 overflow-x-auto">
              <table data-testid="wta-grade-modality-table" className="w-full text-[11px]">
                <thead className="bg-violet-100">
                  <tr>
                    <th className="text-left px-2 py-1.5">Grade</th>
                    <th className="text-left px-2 py-1.5">Modality</th>
                    <th className="text-right px-2 py-1.5 text-rose-700">Neither</th>
                    <th className="text-right px-2 py-1.5 text-amber-900">ELD only</th>
                    <th className="text-right px-2 py-1.5 text-amber-900">KLU only</th>
                    <th className="text-right px-2 py-1.5 text-emerald-700">Both</th>
                    <th className="text-right px-2 py-1.5">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(wtaSummary.by_grade_modality || {})
                    .sort(([a], [b]) => a.localeCompare(b))
                    .flatMap(([grade, mods]) =>
                      Object.entries(mods).map(([modality, c]) => (
                        <tr key={`${grade}-${modality}`} className="border-t border-violet-100">
                          <td className="px-2 py-1 font-semibold">{grade}</td>
                          <td className="px-2 py-1">{modality}</td>
                          <td className={`px-2 py-1 text-right ${c.neither > 0 ? 'text-rose-700 font-bold' : 'text-rose-300'}`}>{c.neither}</td>
                          <td className="px-2 py-1 text-right text-amber-700">{c.with_eld - c.with_both}</td>
                          <td className="px-2 py-1 text-right text-amber-700">{c.with_klu - c.with_both}</td>
                          <td className="px-2 py-1 text-right text-emerald-700">{c.with_both}</td>
                          <td className="px-2 py-1 text-right font-semibold">{c.total}</td>
                        </tr>
                      ))
                    )
                  }
                </tbody>
              </table>
            </div>

            <div className="border border-violet-300 bg-white rounded p-3">
              <div className="text-xs font-bold text-violet-900 mb-1 inline-flex items-center gap-2">
                <Upload size={12} /> WIDA tag manifest (quarantine upload)
              </div>
              <div className="text-[11px] text-violet-800 mb-2">
                Upload a content-team-approved CSV with columns{' '}
                <span className="font-mono">question_id, eld_standard, key_language_use, reviewer, reviewed_at, notes</span>.{' '}
                <span className="font-mono">eld_standard</span> must be 1-5;{' '}
                <span className="font-mono">key_language_use</span> must be Narrate / Inform / Explain / Argue.{' '}
                File parked in <span className="font-mono">wida_tag_manifest_pending</span> for review.{' '}
                <span className="font-semibold">No question records are modified by this upload.</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  data-testid="wta-manifest-upload"
                  aria-label="WIDA tag manifest CSV upload"
                  type="file"
                  accept=".csv"
                  onChange={uploadWtaManifest}
                  disabled={wtaUploading}
                  className="text-[11px]"
                />
                {wtaUploading && (
                  <span className="text-[11px] text-violet-700 inline-flex items-center gap-1">
                    <Loader2 size={11} className="animate-spin" /> Validating + storing…
                  </span>
                )}
              </div>

              {wtaManifests.length > 0 && (
                <div className="mt-3 border-t border-violet-100 pt-2">
                  <div className="text-[10px] uppercase tracking-wider text-violet-700 font-semibold mb-1">
                    Pending manifests ({wtaManifests.length})
                  </div>
                  <div className="space-y-1">
                    {wtaManifests.slice(0, 5).map((m) => (
                      <div key={m.manifest_id} data-testid={`wta-manifest-${m.manifest_id}`} className="text-[10px] flex items-center gap-2 font-mono">
                        <span className="text-violet-700">{m.uploaded_at?.slice(0, 19)}</span>
                        <span className="font-semibold">{m.filename}</span>
                        <span className="text-violet-700">{m.row_count} rows</span>
                        {m.warnings && m.warnings.length > 0 && (
                          <span className="text-rose-700">{m.warnings.length} warnings</span>
                        )}
                        <span className="ml-auto text-violet-600">{m.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Assessment Stats */}
        <div className="border-2 border-gray-100 p-5">
          <h3 className="font-bold text-sm mb-3">Assessment Activity</h3>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Completed</span>
              <span className="font-bold text-green-700">{stats.assessments?.completed}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">In Progress</span>
              <span className="font-bold text-amber-700">{stats.assessments?.active}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Total Responses</span>
              <span className="font-bold">{stats.assessments?.total_responses?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Active Assignments</span>
              <span className="font-bold">{stats.assignments}</span>
            </div>
          </div>
        </div>

        {/* License Distribution */}
        <div className="border-2 border-gray-100 p-5">
          <h3 className="font-bold text-sm mb-3">License Distribution</h3>
          <div className="space-y-2">
            {Object.entries(stats.license_distribution || {}).map(([tier, count]) => (
              <div key={tier} className="flex items-center justify-between text-sm">
                <span className={`px-2 py-0.5 text-xs font-semibold border ${LICENSE_TIERS[tier]?.color || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                  {LICENSE_TIERS[tier]?.label || 'No License'}
                </span>
                <span className="font-bold">{count} school{count !== 1 ? 's' : ''}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Disk / System Health */}
        <DiskStatusWidget />
      </div>

      {/* Recent Activity */}
      <div className="border-2 border-gray-100 p-5">
        <h3 className="font-bold text-sm mb-3">Last 7 Days</h3>
        <div className="flex gap-8">
          <div>
            <div className="text-2xl font-black text-[#002FA7]">{stats.recent_activity?.logins_7d}</div>
            <div className="text-xs text-gray-500">Logins</div>
          </div>
          <div>
            <div className="text-2xl font-black text-orange-600">{stats.recent_activity?.exams_7d}</div>
            <div className="text-xs text-gray-500">Exams Started</div>
          </div>
        </div>
      </div>

      {/* Platform Spider Chart */}
      {spiderData.districts?.length > 0 && (
        <ProficiencySpiderChart
          series={spiderData.districts.filter(d => d.listening || d.speaking || d.reading || d.writing).map(d => ({ name: d.district_name, ...d }))}
          overlay={spiderData.platform_average ? { name: 'Platform Average', ...spiderData.platform_average } : null}
          title="Platform-Wide Modality Performance"
          subtitle="Per-district MOSAIC proficiency averages with platform average overlay (gray dashed)"
          height={340}
        />
      )}
    </div>
  );
};

// ========== DISTRICTS TAB ==========
const DistrictsTab = ({ districts, onRefresh }) => {
  const [expanded, setExpanded] = useState(null);
  const [showAddDistrict, setShowAddDistrict] = useState(false);
  const [newDistrict, setNewDistrict] = useState({ name: '', code: '', state: 'NY', admin_email: '', admin_name: '' });
  const [showAddSchool, setShowAddSchool] = useState(null); // district_id
  const [newSchool, setNewSchool] = useState({ name: '', license_tier: 'all', dbn: '', borough: '', address: '', phone: '', principal_name: '', principal_email: '', mosaic_admin_name: '', mosaic_admin_email: '', mosaic_admin_phone: '', it_contact_name: '', it_contact_email: '', it_contact_phone: '' });
  const [saving, setSaving] = useState(false);
  // v6.32.217 — cascade-delete confirmation modal state
  const [cascadeModal, setCascadeModal] = useState(null); // {kind:'district'|'school', id, name, preview}
  const [cascadeLoading, setCascadeLoading] = useState(false);

  const openCascadePreview = async (kind, id, name) => {
    setCascadeLoading(true);
    try {
      const url = kind === 'district'
        ? `${API}/superadmin/districts/${id}/cascade-preview`
        : `${API}/superadmin/schools/${id}/cascade-preview`;
      const res = await axios.get(url);
      setCascadeModal({ kind, id, name, preview: res.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to load ${kind} cascade preview`);
    } finally {
      setCascadeLoading(false);
    }
  };

  const confirmCascadeDelete = async () => {
    if (!cascadeModal) return;
    const { kind, id, name } = cascadeModal;
    setCascadeLoading(true);
    try {
      const url = kind === 'district'
        ? `${API}/superadmin/districts/${id}/hard-delete`
        : `${API}/superadmin/schools/${id}/hard-delete`;
      await axios.delete(url);
      toast.success(`${kind === 'district' ? 'District' : 'School'} "${name}" deleted`);
      setCascadeModal(null);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to delete ${kind}`);
    } finally {
      setCascadeLoading(false);
    }
  };

  const handleAddDistrict = async () => {
    if (!newDistrict.name.trim()) return toast.error('District name is required');
    const inviteRequested = newDistrict.admin_email.trim() || newDistrict.admin_name.trim();
    if (inviteRequested) {
      if (!newDistrict.admin_email.trim()) return toast.error('Admin email is required when inviting an admin');
      if (!newDistrict.admin_name.trim()) return toast.error('Admin name is required when inviting an admin');
    }
    setSaving(true);
    try {
      // v6.32.219 (FU-005) — single round-trip: create district + (optionally) invite admin.
      const payload = {
        district_name: newDistrict.name.trim(),
        code: newDistrict.code.trim(),
        state: newDistrict.state.trim() || 'NY',
        admin_email: newDistrict.admin_email.trim().toLowerCase() || undefined,
        admin_name: newDistrict.admin_name.trim() || undefined,
      };
      const res = await axios.post(`${API}/superadmin/districts/with-admin`, payload);
      if (res.data?.admin) {
        if (res.data.admin.invite_sent) {
          toast.success(`District created & invite sent to ${res.data.admin.email}`);
        } else {
          toast.warning(`District created — invite email did NOT send${res.data.admin.invite_error ? ': ' + res.data.admin.invite_error : ''}`);
        }
      } else {
        toast.success('District created');
      }
      setNewDistrict({ name: '', code: '', state: 'NY', admin_email: '', admin_name: '' });
      setShowAddDistrict(false);
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create district'); }
    finally { setSaving(false); }
  };

  const handleAddSchool = async (districtId) => {
    if (!newSchool.name.trim()) return toast.error('School name is required');
    setSaving(true);
    try {
      await axios.post(`${API}/superadmin/schools`, { ...newSchool, district_id: districtId });
      toast.success('School created');
      setNewSchool({ name: '', license_tier: 'all', dbn: '', borough: '', address: '', phone: '', principal_name: '', principal_email: '', mosaic_admin_name: '', mosaic_admin_email: '', mosaic_admin_phone: '', it_contact_name: '', it_contact_email: '', it_contact_phone: '' });
      setShowAddSchool(null);
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create school'); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="superadmin-districts">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-base">All Districts ({districts.length})</h3>
        <Button
          size="sm"
          onClick={() => setShowAddDistrict(!showAddDistrict)}
          className="bg-[#002FA7] text-white hover:bg-[#001f6f] text-xs"
          data-testid="add-district-btn"
        >
          <Plus size={14} className="mr-1" /> Add District
        </Button>
      </div>

      {showAddDistrict && (
        <div className="border-2 border-[#002FA7] bg-blue-50/50 p-4 mb-4" data-testid="add-district-form">
          <h4 className="font-bold text-sm mb-3">New District</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">District Name *</Label>
              <Input
                value={newDistrict.name}
                onChange={e => setNewDistrict({ ...newDistrict, name: e.target.value })}
                placeholder="e.g., NYC District 20"
                className="mt-1"
                data-testid="district-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">Code</Label>
              <Input
                value={newDistrict.code}
                onChange={e => setNewDistrict({ ...newDistrict, code: e.target.value })}
                placeholder="e.g., D20"
                className="mt-1"
                data-testid="district-code-input"
              />
            </div>
            <div>
              <Label className="text-xs">State</Label>
              <Input
                value={newDistrict.state}
                onChange={e => setNewDistrict({ ...newDistrict, state: e.target.value })}
                placeholder="NY"
                className="mt-1"
                data-testid="district-state-input"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" onClick={handleAddDistrict} disabled={saving} className="bg-[#002FA7] text-white hover:bg-[#001f6f] text-xs" data-testid="save-district-btn">
              {saving ? 'Saving...' : (newDistrict.admin_email ? 'Create District + Invite Admin' : 'Create District')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowAddDistrict(false)} className="text-xs">Cancel</Button>
          </div>

          {/* v6.32.219 (FU-005) — Optional district-admin invite section. */}
          <div className="mt-5 pt-4 border-t border-blue-200">
            <div className="flex items-baseline gap-2 mb-2">
              <h5 className="font-bold text-xs uppercase tracking-wide text-[#002FA7]">Invite District Admin (optional)</h5>
              <span className="text-[10px] text-gray-500">Leave blank to skip and invite later from the Users tab</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Admin Name</Label>
                <Input
                  value={newDistrict.admin_name}
                  onChange={e => setNewDistrict({ ...newDistrict, admin_name: e.target.value })}
                  placeholder="e.g., Dr. Maria Santos"
                  className="mt-1"
                  data-testid="district-admin-name-input"
                />
              </div>
              <div>
                <Label className="text-xs">Admin Email</Label>
                <Input
                  value={newDistrict.admin_email}
                  onChange={e => setNewDistrict({ ...newDistrict, admin_email: e.target.value })}
                  placeholder="e.g., msantos@district.nyc"
                  type="email"
                  className="mt-1"
                  data-testid="district-admin-email-input"
                />
              </div>
            </div>
            <p className="text-[10px] text-gray-500 mt-2">A magic-link welcome email is sent to this address. They set their own password on first sign-in.</p>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {districts.map(d => (
          <div key={d.district_id} className="border-2 border-gray-200" data-testid={`district-${d.district_id}`}>
            <div
              className="p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50"
              onClick={() => setExpanded(expanded === d.district_id ? null : d.district_id)}
            >
              <div className="w-10 h-10 bg-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0">
                <Building2 size={20} />
              </div>
              <div className="flex-1">
                <div className="font-bold text-sm">{d.name}</div>
                <div className="text-xs text-gray-500">
                  {d.school_count} schools | {d.user_count} users | {d.student_count} students
                </div>
              </div>
              <Button
                size="sm" variant="ghost"
                onClick={(e) => { e.stopPropagation(); openCascadePreview('district', d.district_id, d.name); }}
                className="h-8 px-2 text-rose-700 hover:bg-rose-50"
                title="Permanently delete this district (cascade-deletes all schools, classes, users, and assignments). Refused if any student has submitted exam data."
                data-testid={`hard-delete-district-${d.district_id}`}
              >
                <Trash2 size={14} />
              </Button>
              {expanded === d.district_id ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
            </div>
            {expanded === d.district_id && (
              <div className="border-t border-gray-200 p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-gray-500 uppercase">Schools</div>
                  <Button
                    size="sm" variant="outline"
                    onClick={(e) => { e.stopPropagation(); setShowAddSchool(showAddSchool === d.district_id ? null : d.district_id); }}
                    className="text-xs h-7"
                    data-testid={`add-school-btn-${d.district_id}`}
                  >
                    <Plus size={12} className="mr-1" /> Add School
                  </Button>
                </div>

                {showAddSchool === d.district_id && (
                  <div className="bg-white border border-blue-200 p-3 mb-3" data-testid="add-school-form">
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">School Info</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                      <div>
                        <Label className="text-xs">School Name *</Label>
                        <Input value={newSchool.name} onChange={e => setNewSchool({ ...newSchool, name: e.target.value })} placeholder="e.g., PS 124" className="mt-1" data-testid="school-name-input" />
                      </div>
                      <div>
                        <Label className="text-xs">DBN</Label>
                        <Input value={newSchool.dbn} onChange={e => setNewSchool({ ...newSchool, dbn: e.target.value })} placeholder="e.g., 20K124" className="mt-1" data-testid="school-dbn-input" />
                      </div>
                      <div>
                        <Label className="text-xs">Borough</Label>
                        <Input value={newSchool.borough} onChange={e => setNewSchool({ ...newSchool, borough: e.target.value })} placeholder="e.g., Brooklyn" className="mt-1" data-testid="school-borough-input" />
                      </div>
                      <div>
                        <Label className="text-xs">License Tier</Label>
                        <select value={newSchool.license_tier} onChange={e => setNewSchool({ ...newSchool, license_tier: e.target.value })} className="w-full mt-1 border border-gray-300 text-sm p-2 rounded" data-testid="school-license-select">
                          {Object.entries(LICENSE_TIERS).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <Label className="text-xs">Address</Label>
                        <Input value={newSchool.address} onChange={e => setNewSchool({ ...newSchool, address: e.target.value })} placeholder="Full street address" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">School Phone</Label>
                        <Input value={newSchool.phone} onChange={e => setNewSchool({ ...newSchool, phone: e.target.value })} placeholder="(212) 555-0124" className="mt-1" />
                      </div>
                    </div>
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Principal</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <div>
                        <Label className="text-xs">Principal Name</Label>
                        <Input value={newSchool.principal_name} onChange={e => setNewSchool({ ...newSchool, principal_name: e.target.value })} placeholder="Dr. Maria Santos" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Principal Email</Label>
                        <Input value={newSchool.principal_email} onChange={e => setNewSchool({ ...newSchool, principal_email: e.target.value })} placeholder="msantos@school.nyc" className="mt-1" />
                      </div>
                    </div>
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">MOSAIC Admin</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <div>
                        <Label className="text-xs">Admin Name</Label>
                        <Input value={newSchool.mosaic_admin_name} onChange={e => setNewSchool({ ...newSchool, mosaic_admin_name: e.target.value })} placeholder="James Rodriguez" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Admin Email</Label>
                        <Input value={newSchool.mosaic_admin_email} onChange={e => setNewSchool({ ...newSchool, mosaic_admin_email: e.target.value })} placeholder="jrodriguez@school.nyc" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">Admin Phone</Label>
                        <Input value={newSchool.mosaic_admin_phone} onChange={e => setNewSchool({ ...newSchool, mosaic_admin_phone: e.target.value })} placeholder="(212) 555-0125" className="mt-1" />
                      </div>
                    </div>
                    <div className="text-xs font-semibold text-gray-500 uppercase mb-2">IT Contact</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                      <div>
                        <Label className="text-xs">IT Name</Label>
                        <Input value={newSchool.it_contact_name} onChange={e => setNewSchool({ ...newSchool, it_contact_name: e.target.value })} placeholder="Sarah Kim" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">IT Email</Label>
                        <Input value={newSchool.it_contact_email} onChange={e => setNewSchool({ ...newSchool, it_contact_email: e.target.value })} placeholder="skim@school.nyc" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs">IT Phone</Label>
                        <Input value={newSchool.it_contact_phone} onChange={e => setNewSchool({ ...newSchool, it_contact_phone: e.target.value })} placeholder="(212) 555-0126" className="mt-1" />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleAddSchool(d.district_id)} disabled={saving} className="bg-[#002FA7] text-white hover:bg-[#001f6f] text-xs" data-testid="save-school-btn">
                        {saving ? 'Saving...' : 'Create School'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowAddSchool(null)} className="text-xs">Cancel</Button>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  {d.schools && d.schools.map(s => (
                    <div key={s.school_id} className="flex items-center justify-between bg-white p-3 border border-gray-200">
                      <div>
                        <span className="font-medium text-sm">{s.name}</span>
                        <span className="text-xs text-gray-400 ml-2">{s.school_id}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 font-semibold border ${LICENSE_TIERS[s.license_tier]?.color || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                          {LICENSE_TIERS[s.license_tier]?.label || 'No License'}
                        </span>
                        <Button
                          size="sm" variant="ghost"
                          onClick={(e) => { e.stopPropagation(); openCascadePreview('school', s.school_id, s.name); }}
                          className="h-7 px-2 text-rose-700 hover:bg-rose-50"
                          title="Permanently delete this school (cascade-deletes its classes + users + scoped assignments). Refused if any student has submitted exam data."
                          data-testid={`hard-delete-school-${s.school_id}`}
                        >
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {(!d.schools || d.schools.length === 0) && (
                    <div className="text-xs text-gray-400 italic py-2">No schools in this district yet. Click "Add School" to create one.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* v6.32.217 — Cascade-delete confirmation modal */}
      {cascadeModal && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          data-testid="cascade-delete-modal"
        >
          <div className="bg-white border-4 border-rose-600 max-w-xl w-full p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
                <Trash2 size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg text-rose-900">
                  Permanently delete {cascadeModal.kind}: {cascadeModal.name}?
                </h3>
                <div className="text-xs text-gray-500 mt-1">
                  This action cannot be undone. Review the cascade carefully.
                </div>
              </div>
            </div>

            {!cascadeModal.preview.deletable && (
              <div className="bg-rose-50 border-2 border-rose-300 p-3 mb-4 text-sm text-rose-900" data-testid="cascade-blocker">
                <div className="font-bold mb-1">🛑 Cannot delete</div>
                <div className="text-xs">{cascadeModal.preview.blocker}</div>
              </div>
            )}

            <div className="border-2 border-gray-200 bg-gray-50 p-3 mb-4 space-y-1.5 text-sm" data-testid="cascade-preview-counts">
              {cascadeModal.kind === 'district' && (
                <div><b>{cascadeModal.preview.schools_count}</b> schools will be deleted{cascadeModal.preview.school_names_sample?.length ? ` (e.g., ${cascadeModal.preview.school_names_sample.join(', ')})` : ''}.</div>
              )}
              <div><b>{cascadeModal.preview.classes_count}</b> classes will be deleted.</div>
              <div><b>{cascadeModal.preview.users_total}</b> users will be deleted
                {cascadeModal.preview.users_count_by_role && Object.keys(cascadeModal.preview.users_count_by_role).length > 0 && (
                  <span className="text-xs text-gray-500"> ({Object.entries(cascadeModal.preview.users_count_by_role).map(([r, n]) => `${n} ${r}`).join(', ')})</span>
                )}.
              </div>
              <div><b>{cascadeModal.preview.assignments_count}</b> assignments will be {cascadeModal.kind === 'district' ? 'deleted' : 'unscoped / deleted'}.</div>
              {cascadeModal.preview.students_with_data_count > 0 && (
                <div className="text-rose-700"><b>⚠️ {cascadeModal.preview.students_with_data_count}</b> student(s) in scope have submitted exam data.</div>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => setCascadeModal(null)} className="text-xs" data-testid="cascade-cancel-btn">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={confirmCascadeDelete}
                disabled={!cascadeModal.preview.deletable || cascadeLoading}
                className="bg-rose-700 hover:bg-rose-800 text-white text-xs disabled:bg-gray-300 disabled:text-gray-500"
                data-testid="cascade-confirm-btn"
              >
                {cascadeLoading ? 'Deleting…' : `Permanently delete ${cascadeModal.kind}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========== SCHOOLS TAB ==========
const SchoolsTab = ({ onRefresh }) => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editTier, setEditTier] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [newSchool, setNewSchool] = useState({ name: '', district_id: '', license_tier: 'all', dbn: '', borough: '', address: '', phone: '', principal_name: '', principal_email: '', mosaic_admin_name: '', mosaic_admin_email: '', mosaic_admin_phone: '', it_contact_name: '', it_contact_email: '', it_contact_phone: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSchools();
    fetchDistricts();
  }, []);

  // v6.32.218 — re-fetch districts whenever the Add-School modal opens, so a
  // district created in another tab is immediately selectable here.
  useEffect(() => {
    if (showAdd) fetchDistricts();
  }, [showAdd]);

  const fetchSchools = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/schools`);
      setSchools(res.data.schools || []);
    } catch { toast.error('Failed to load schools'); }
    finally { setLoading(false); }
  };

  const fetchDistricts = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/districts`);
      setDistricts(res.data.districts || []);
    } catch (e) {
      // v6.32.218 — surface failure; previous silent catch made stale-empty
      // dropdowns indistinguishable from real fetch failures.
      console.error('fetchDistricts failed', e);
      toast.error(e.response?.data?.detail || 'Failed to load districts');
    }
  };

  const saveLicense = async (schoolId) => {
    try {
      await axios.patch(`${API}/superadmin/schools/${schoolId}`, { license_tier: editTier });
      toast.success('License updated');
      setEditingId(null);
      fetchSchools();
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  // v6.34.11 — Moi-Mode Stage 2: school-level opt-in toggle inside the
  // "Accommodations & Modes" modal. Per PRD addendum §5.6, the warning copy
  // explicitly references ERMA disclosure as a precondition.
  const [modesModal, setModesModal] = useState(null); // {school_id, name, moi_mode_enabled}
  const [modesSaving, setModesSaving] = useState(false);

  const toggleMoiMode = async () => {
    if (!modesModal) return;
    setModesSaving(true);
    try {
      const newValue = !modesModal.moi_mode_enabled;
      await axios.patch(
        `${API}/superadmin/schools/${modesModal.school_id}/moi-mode`,
        { enabled: newValue },
      );
      toast.success(`Moi-Mode ${newValue ? 'enabled' : 'disabled'} for ${modesModal.name}`);
      setModesModal(null);
      fetchSchools();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to toggle Moi-Mode');
    } finally {
      setModesSaving(false);
    }
  };

  const handleAddSchool = async () => {
    if (!newSchool.name.trim()) return toast.error('School name is required');
    if (!newSchool.district_id) return toast.error('Please select a district');
    setSaving(true);
    try {
      await axios.post(`${API}/superadmin/schools`, newSchool);
      toast.success('School created');
      setNewSchool({ name: '', district_id: '', license_tier: 'all', dbn: '', borough: '', address: '', phone: '', principal_name: '', principal_email: '', mosaic_admin_name: '', mosaic_admin_email: '', mosaic_admin_phone: '', it_contact_name: '', it_contact_email: '', it_contact_phone: '' });
      setShowAdd(false);
      fetchSchools();
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create school'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-16 text-center"><div className="loading-spinner mx-auto"></div></div>;

  return (
    <div data-testid="superadmin-schools">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-base">All Schools ({schools.length})</h3>
        <Button
          size="sm"
          onClick={() => setShowAdd(!showAdd)}
          className="bg-[#002FA7] text-white hover:bg-[#001f6f] text-xs"
          data-testid="add-school-global-btn"
        >
          <Plus size={14} className="mr-1" /> Add School
        </Button>
      </div>

      {showAdd && (
        <div className="border-2 border-[#002FA7] bg-blue-50/50 p-4 mb-4" data-testid="add-school-global-form">
          <h4 className="font-bold text-sm mb-3">New School</h4>

          {/* Row 1: Basic Info */}
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">School Information</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
            <div>
              <Label className="text-xs">School Name *</Label>
              <Input value={newSchool.name} onChange={e => setNewSchool({ ...newSchool, name: e.target.value })} placeholder="e.g., PS 124 Yung Wing" className="mt-1" data-testid="school-name-global-input" />
            </div>
            <div>
              <Label className="text-xs">District *</Label>
              <select value={newSchool.district_id} onChange={e => setNewSchool({ ...newSchool, district_id: e.target.value })} className="w-full mt-1 border border-gray-300 text-sm p-2 rounded" data-testid="school-district-global-select">
                <option value="">Select district...</option>
                {districts.map(d => (<option key={d.district_id} value={d.district_id}>{d.name}</option>))}
              </select>
            </div>
            <div>
              <Label className="text-xs">DBN</Label>
              <Input value={newSchool.dbn} onChange={e => setNewSchool({ ...newSchool, dbn: e.target.value })} placeholder="e.g., 02M124" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Borough</Label>
              <Input value={newSchool.borough} onChange={e => setNewSchool({ ...newSchool, borough: e.target.value })} placeholder="e.g., Manhattan" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">License Tier</Label>
              <select value={newSchool.license_tier} onChange={e => setNewSchool({ ...newSchool, license_tier: e.target.value })} className="w-full mt-1 border border-gray-300 text-sm p-2 rounded" data-testid="school-license-global-select">
                {Object.entries(LICENSE_TIERS).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div>
              <Label className="text-xs">Address</Label>
              <Input value={newSchool.address} onChange={e => setNewSchool({ ...newSchool, address: e.target.value })} placeholder="e.g., 40 Division St, New York, NY 10002" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">School Phone</Label>
              <Input value={newSchool.phone} onChange={e => setNewSchool({ ...newSchool, phone: e.target.value })} placeholder="e.g., (212) 555-0124" className="mt-1" />
            </div>
          </div>

          {/* Row 2: Principal */}
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Principal</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div>
              <Label className="text-xs">Principal Name</Label>
              <Input value={newSchool.principal_name} onChange={e => setNewSchool({ ...newSchool, principal_name: e.target.value })} placeholder="e.g., Dr. Maria Santos" className="mt-1" data-testid="school-principal-name" />
            </div>
            <div>
              <Label className="text-xs">Principal Email</Label>
              <Input value={newSchool.principal_email} onChange={e => setNewSchool({ ...newSchool, principal_email: e.target.value })} placeholder="e.g., msantos@school.nyc" className="mt-1" data-testid="school-principal-email" />
            </div>
          </div>

          {/* Row 3: MOSAIC Admin */}
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">MOSAIC Admin Contact</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div>
              <Label className="text-xs">Admin Name</Label>
              <Input value={newSchool.mosaic_admin_name} onChange={e => setNewSchool({ ...newSchool, mosaic_admin_name: e.target.value })} placeholder="e.g., James Rodriguez" className="mt-1" data-testid="school-mosaic-admin-name" />
            </div>
            <div>
              <Label className="text-xs">Admin Email</Label>
              <Input value={newSchool.mosaic_admin_email} onChange={e => setNewSchool({ ...newSchool, mosaic_admin_email: e.target.value })} placeholder="e.g., jrodriguez@school.nyc" className="mt-1" data-testid="school-mosaic-admin-email" />
            </div>
            <div>
              <Label className="text-xs">Admin Phone</Label>
              <Input value={newSchool.mosaic_admin_phone} onChange={e => setNewSchool({ ...newSchool, mosaic_admin_phone: e.target.value })} placeholder="e.g., (212) 555-0125" className="mt-1" data-testid="school-mosaic-admin-phone" />
            </div>
          </div>

          {/* Row 4: IT Contact */}
          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">IT Contact</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div>
              <Label className="text-xs">IT Contact Name</Label>
              <Input value={newSchool.it_contact_name} onChange={e => setNewSchool({ ...newSchool, it_contact_name: e.target.value })} placeholder="e.g., Sarah Kim" className="mt-1" data-testid="school-it-name" />
            </div>
            <div>
              <Label className="text-xs">IT Contact Email</Label>
              <Input value={newSchool.it_contact_email} onChange={e => setNewSchool({ ...newSchool, it_contact_email: e.target.value })} placeholder="e.g., skim@school.nyc" className="mt-1" data-testid="school-it-email" />
            </div>
            <div>
              <Label className="text-xs">IT Contact Phone</Label>
              <Input value={newSchool.it_contact_phone} onChange={e => setNewSchool({ ...newSchool, it_contact_phone: e.target.value })} placeholder="e.g., (212) 555-0126" className="mt-1" data-testid="school-it-phone" />
            </div>
          </div>

          <div className="flex gap-2">
            <Button size="sm" onClick={handleAddSchool} disabled={saving} className="bg-[#002FA7] text-white hover:bg-[#001f6f] text-xs" data-testid="save-school-global-btn">
              {saving ? 'Saving...' : 'Create School'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="text-xs">Cancel</Button>
          </div>
        </div>
      )}

      <div className="border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-3 font-semibold text-gray-600">School</th>
              <th className="text-left p-3 font-semibold text-gray-600">Students</th>
              <th className="text-left p-3 font-semibold text-gray-600">Teachers</th>
              <th className="text-left p-3 font-semibold text-gray-600">Classes</th>
              <th className="text-left p-3 font-semibold text-gray-600">Exams</th>
              <th className="text-left p-3 font-semibold text-gray-600">License</th>
              <th className="text-left p-3 font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody>
            {schools.map(s => (
              <tr key={s.school_id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`school-row-${s.school_id}`}>
                <td className="p-3">
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-gray-400">{s.district_id}</div>
                </td>
                <td className="p-3 font-medium">{s.student_count}</td>
                <td className="p-3 font-medium">{s.teacher_count}</td>
                <td className="p-3 font-medium">{s.class_count}</td>
                <td className="p-3 font-medium">{s.exam_count}</td>
                <td className="p-3">
                  {editingId === s.school_id ? (
                    <select
                      value={editTier}
                      onChange={(e) => setEditTier(e.target.value)}
                      className="border border-gray-300 text-xs p-1"
                      data-testid={`license-select-${s.school_id}`}
                    >
                      {Object.entries(LICENSE_TIERS).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                  ) : (
                    <span className={`text-xs px-2 py-0.5 font-semibold border ${LICENSE_TIERS[s.license_tier]?.color || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                      {LICENSE_TIERS[s.license_tier]?.label || 'No License'}
                    </span>
                  )}
                </td>
                <td className="p-3">
                  {editingId === s.school_id ? (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => saveLicense(s.school_id)} className="h-7 w-7 p-0 text-green-600" data-testid={`save-license-${s.school_id}`}>
                        <Check size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-7 w-7 p-0 text-gray-400">
                        <X size={14} />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => { setEditingId(s.school_id); setEditTier(s.license_tier || 'all'); }}
                      className="h-7 px-2 text-xs"
                      data-testid={`edit-license-${s.school_id}`}
                    >
                      <Edit2 size={12} className="mr-1" /> License
                    </Button>
                  )}
                  {/* v6.34.11 — Accommodations & Modes (Moi-Mode school opt-in) */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setModesModal({
                      school_id: s.school_id,
                      name: s.name,
                      moi_mode_enabled: Boolean(s.moi_mode_enabled),
                    })}
                    className="h-7 px-2 text-xs ml-1"
                    data-testid={`open-modes-modal-${s.school_id}`}
                  >
                    <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${s.moi_mode_enabled ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    Modes
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* v6.34.11 — Moi-Mode Stage 2: "Accommodations & Modes" modal */}
      {modesModal && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          data-testid="modes-modal-overlay"
          onClick={() => !modesSaving && setModesModal(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="modes-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900">Accommodations &amp; Modes</h3>
                <div className="text-sm text-slate-500">{modesModal.name}</div>
              </div>
              <button
                type="button"
                onClick={() => setModesModal(null)}
                className="text-slate-400 hover:text-slate-700"
                disabled={modesSaving}
                data-testid="modes-modal-close-btn"
              >
                <X size={18} />
              </button>
            </div>

            <div className="border border-slate-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="font-semibold text-slate-900">Moi-Mode (K-2)</div>
                  <div className="text-xs text-slate-500">Friendly mascot on welcome, transition, and completion screens.</div>
                </div>
                <span
                  className={`text-xs px-2 py-1 rounded-full font-semibold ${modesModal.moi_mode_enabled ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-slate-100 text-slate-600 border border-slate-300'}`}
                  data-testid="moi-mode-current-state"
                >
                  {modesModal.moi_mode_enabled ? 'ENABLED' : 'OFF (default)'}
                </span>
              </div>
              <div className="text-xs text-slate-600 mt-3 space-y-1">
                <div>• Default OFF for every school (matches real WIDA ACCESS).</div>
                <div>• Renders ONLY on welcome / transition / end-of-section / completion screens — never during a live item.</div>
                <div>• Pre-recorded audio greetings only. No runtime content-engine. No scaffolds. No hints.</div>
                <div>• K-2 students only. Grade 3+ students never see Moi.</div>
              </div>
              <div className="mt-3 p-2 rounded bg-amber-50 border border-amber-200 text-xs text-amber-900" data-testid="moi-mode-erma-warning">
                <b>ERMA disclosure required.</b> Do not enable in production until your ERMA acknowledgement covering the student-facing mascot surface has landed.
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setModesModal(null)}
                disabled={modesSaving}
                data-testid="modes-modal-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={toggleMoiMode}
                disabled={modesSaving}
                className={modesModal.moi_mode_enabled ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                data-testid="moi-mode-toggle-btn"
              >
                {modesSaving ? 'Saving…' : (modesModal.moi_mode_enabled ? 'Disable Moi-Mode' : 'Enable Moi-Mode')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ========== USERS TAB ==========
const UsersTab = ({ navigate, initialRoleFilter = '' }) => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState(initialRoleFilter);
  const [impersonating, setImpersonating] = useState(false);
  // v6.32.207 (FU-006) — Create User modal state
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', name: '', role: 'teacher', district_id: '', school_id: '' });
  const [creating, setCreating] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [schools, setSchools] = useState([]);

  const fetchUsers = async (p = 1, role) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 50 });
      if (search) params.append('search', search);
      const activeRole = role !== undefined ? role : roleFilter;
      if (activeRole) params.append('role', activeRole);
      const res = await axios.get(`${API}/superadmin/users?${params}`);
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setPage(p);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(1, initialRoleFilter); }, [initialRoleFilter]);

  // v6.32.207 — fetch districts + schools when modal opens so the create form has dropdowns
  useEffect(() => {
    if (!showCreate) return;
    (async () => {
      try {
        const [dRes, sRes] = await Promise.all([
          axios.get(`${API}/superadmin/districts`),
          axios.get(`${API}/superadmin/schools`),
        ]);
        setDistricts(dRes.data.districts || []);
        setSchools(sRes.data.schools || []);
      } catch {
        toast.error('Failed to load districts/schools for the form');
      }
    })();
  }, [showCreate]);

  const handleSearch = () => fetchUsers(1);

  const createUser = async () => {
    if (!createForm.email || !createForm.name) {
      toast.error('Email and name are required');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        email: createForm.email.trim().toLowerCase(),
        name: createForm.name.trim(),
        role: createForm.role,
        district_id: createForm.district_id || undefined,
        school_id: createForm.school_id || undefined,
        send_invite: true,
      };
      const res = await axios.post(`${API}/superadmin/users`, payload);
      if (res.data.invite_sent) {
        toast.success(`User created — invite email sent to ${payload.email}`);
      } else {
        toast.warning(`User created — invite email did NOT send${res.data.invite_error ? ': ' + res.data.invite_error : ''}`);
      }
      setShowCreate(false);
      setCreateForm({ email: '', name: '', role: 'teacher', district_id: '', school_id: '' });
      fetchUsers(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const resendInvite = async (userId, email) => {
    try {
      const res = await axios.post(`${API}/superadmin/users/${userId}/resend-invite`, {});
      if (res.data.invite_sent) toast.success(`Invite resent to ${email}`);
      else toast.warning('Resend failed — check backend logs');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const impersonate = async (userId, userName) => {
    setImpersonating(true);
    try {
      const res = await axios.post(`${API}/superadmin/impersonate/${userId}`, {});
      if (res.data.session_token) {
        // Save original super admin token so we can exit impersonation
        const currentToken = localStorage.getItem('mosaic_session_token');
        if (currentToken) {
          localStorage.setItem('mosaic_impersonation_original_token', currentToken);
        }
        // Store the impersonation session token
        localStorage.setItem('mosaic_session_token', res.data.session_token);
        localStorage.setItem('mosaic_impersonating_as', JSON.stringify({
          name: res.data.impersonating?.name || userName,
          role: res.data.impersonating?.role || 'user',
          email: res.data.impersonating?.email || '',
        }));
        toast.success(`Now viewing as ${userName}`);
        // Redirect to the appropriate dashboard for the impersonated role
        const roleRoutes = {
          teacher: '/teacher',
          school_admin: '/admin/school',
          district_admin: '/admin/district',
          student: '/exam/setup/K',
        };
        const targetRole = res.data.impersonating?.role || 'teacher';
        window.location.href = roleRoutes[targetRole] || '/teacher';
      } else {
        toast.error('Impersonation failed — no session token returned');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to impersonate');
    } finally {
      setImpersonating(false);
    }
  };

  const toggleActive = async (userId, currentlyActive) => {
    try {
      if (currentlyActive) {
        await axios.delete(`${API}/superadmin/users/${userId}`);
        toast.success('User disabled');
      } else {
        await axios.patch(`${API}/superadmin/users/${userId}`, { is_active: true });
        toast.success('User enabled');
      }
      fetchUsers(page);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  // v6.32.216 — hard-delete (true row removal) for cleaning up test users.
  // Soft-disable above stays the default user-management action; this is the
  // explicit "remove from list entirely" path. Server-side guards refuse to
  // delete super_admins, self, or students with submitted exam data.
  const hardDelete = async (userId, email) => {
    const ok = window.confirm(
      `Permanently delete user "${email}"?\n\n` +
      `This removes the row from the database. ` +
      `Use this only for test users or never-logged-in invite rows.\n\n` +
      `Students with submitted exam data cannot be hard-deleted — disable them instead.\n\n` +
      `This action cannot be undone.`
    );
    if (!ok) return;
    try {
      await axios.delete(`${API}/superadmin/users/${userId}/hard-delete`);
      toast.success(`Deleted ${email}`);
      fetchUsers(page);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete user');
    }
  };

  return (
    <div data-testid="superadmin-users">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs font-semibold mb-1 block">Search</Label>
          <div className="flex gap-2">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Name or email..."
              className="h-9 border-2 border-gray-300 rounded-none text-sm"
              data-testid="user-search-input"
            />
            <Button onClick={handleSearch} className="btn-sharp h-9 px-3" data-testid="user-search-btn">
              <Search size={14} />
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-xs font-semibold mb-1 block">Role</Label>
          <select
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setTimeout(() => fetchUsers(1, e.target.value), 0); }}
            className="h-9 border-2 border-gray-300 px-3 text-sm"
            data-testid="role-filter"
          >
            <option value="">All Roles</option>
            <option value="student">Student</option>
            <option value="teacher">Teacher</option>
            <option value="school_admin">School Admin</option>
            <option value="district_admin">District Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
        <div className="text-sm text-gray-500 self-end pb-1">{total} users</div>
        <Button
          onClick={() => setShowCreate(true)}
          className="btn-sharp h-9 px-3 ml-auto"
          data-testid="superadmin-add-user-btn"
        >
          <Plus size={14} className="mr-1" /> Add User
        </Button>
      </div>

      {/* v6.32.207 (FU-006) Create User modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="create-user-modal">
          <div className="bg-white border-2 border-gray-900 max-w-lg w-full p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg flex items-center gap-2"><Plus size={18} /> Invite New User</h3>
              <button onClick={() => setShowCreate(false)} className="text-gray-500 hover:text-gray-900" data-testid="create-user-close">
                <X size={20} />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              The user will receive a welcome email with a secure magic link to set their first password. The link expires in 7 days.
            </p>
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold mb-1 block">Email *</Label>
                <Input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder="user@district.org"
                  className="h-9 border-2 border-gray-300 rounded-none text-sm"
                  data-testid="create-user-email"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1 block">Full name *</Label>
                <Input
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="Jane Doe"
                  className="h-9 border-2 border-gray-300 rounded-none text-sm"
                  data-testid="create-user-name"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1 block">Role *</Label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                  className="h-9 w-full border-2 border-gray-300 px-3 text-sm bg-white"
                  data-testid="create-user-role"
                >
                  <option value="teacher">Teacher</option>
                  <option value="school_admin">School Admin</option>
                  <option value="district_admin">District Admin</option>
                  <option value="erma_reviewer">ERMA Reviewer</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1 block">District (optional)</Label>
                <select
                  value={createForm.district_id}
                  onChange={(e) => setCreateForm({ ...createForm, district_id: e.target.value, school_id: '' })}
                  className="h-9 w-full border-2 border-gray-300 px-3 text-sm bg-white"
                  data-testid="create-user-district"
                >
                  <option value="">— None —</option>
                  {districts.map(d => (
                    <option key={d.district_id} value={d.district_id}>{d.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1 block">School (optional)</Label>
                <select
                  value={createForm.school_id}
                  onChange={(e) => setCreateForm({ ...createForm, school_id: e.target.value })}
                  className="h-9 w-full border-2 border-gray-300 px-3 text-sm bg-white"
                  data-testid="create-user-school"
                >
                  <option value="">— None —</option>
                  {schools
                    .filter(s => !createForm.district_id || s.district_id === createForm.district_id)
                    .map(s => (
                      <option key={s.school_id} value={s.school_id}>{s.name}</option>
                    ))}
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button
                onClick={createUser}
                disabled={creating}
                className="btn-sharp flex-1 h-10"
                data-testid="create-user-submit"
              >
                {creating ? <><Loader2 className="animate-spin mr-2" size={14} /> Creating…</> : 'Create + Send Invite'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowCreate(false)}
                disabled={creating}
                className="h-10"
                data-testid="create-user-cancel"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="py-12 text-center"><div className="loading-spinner mx-auto"></div></div>
      ) : (
        <>
          <div className="border border-gray-200 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left p-3 font-semibold text-gray-600">User</th>
                  <th className="text-left p-3 font-semibold text-gray-600">Role</th>
                  <th className="text-left p-3 font-semibold text-gray-600">School</th>
                  <th className="text-left p-3 font-semibold text-gray-600">Status</th>
                  <th className="text-left p-3 font-semibold text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const isActive = u.is_active !== false;
                  return (
                    <tr key={u.user_id} className={`border-t border-gray-100 ${!isActive ? 'opacity-50' : 'hover:bg-gray-50'}`} data-testid={`user-row-${u.user_id}`}>
                      <td className="p-3">
                        <div className="font-medium">{u.name}</div>
                        <div className="text-xs text-gray-400">{u.email}</div>
                      </td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 font-semibold capitalize ${ROLE_COLORS[u.role] || 'bg-gray-100'}`}>
                          {u.role?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="p-3 text-xs text-gray-500">{u.school_id || u.district_id || '—'}</td>
                      <td className="p-3">
                        {isActive ? (
                          <span className="text-xs text-green-600 font-medium flex items-center gap-1"><Unlock size={12} /> Active</span>
                        ) : (
                          <span className="text-xs text-red-600 font-medium flex items-center gap-1"><Lock size={12} /> Disabled</span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button
                            size="sm" variant="ghost"
                            onClick={() => impersonate(u.user_id, u.name)}
                            disabled={impersonating || u.role === 'super_admin'}
                            className="h-7 px-2 text-xs text-[#002FA7]"
                            title="View as this user"
                            data-testid={`impersonate-${u.user_id}`}
                          >
                            <Eye size={12} className="mr-1" /> View As
                          </Button>
                          {!u.password_hash_set && u.role !== 'super_admin' && u.role !== 'student' && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => resendInvite(u.user_id, u.email)}
                              className="h-7 px-2 text-xs text-blue-700"
                              title="Resend the welcome / setup-password email"
                              data-testid={`resend-invite-${u.user_id}`}
                            >
                              <Send size={12} className="mr-1" /> Resend
                            </Button>
                          )}
                          {u.role !== 'super_admin' && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => toggleActive(u.user_id, isActive)}
                              className={`h-7 px-2 text-xs ${isActive ? 'text-red-600' : 'text-green-600'}`}
                              data-testid={`toggle-active-${u.user_id}`}
                            >
                              {isActive ? <><UserX size={12} className="mr-1" /> Disable</> : <><UserCheck size={12} className="mr-1" /> Enable</>}
                            </Button>
                          )}
                          {u.role !== 'super_admin' && (
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => hardDelete(u.user_id, u.email)}
                              className="h-7 px-2 text-xs text-rose-700 hover:bg-rose-50"
                              title="Permanently delete this user row (for cleaning up test/invite-only users)"
                              data-testid={`hard-delete-${u.user_id}`}
                            >
                              <Trash2 size={12} className="mr-1" /> Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-gray-500">Page {page} of {pages}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => fetchUsers(page - 1)} className="h-7 px-3 text-xs">Prev</Button>
                <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => fetchUsers(page + 1)} className="h-7 px-3 text-xs">Next</Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ========== AUDIT TAB ==========
const AuditTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/superadmin/audit-log?limit=100`);
        setLogs(res.data.logs || []);
      } catch { toast.error('Failed to load audit log'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <div className="py-16 text-center"><div className="loading-spinner mx-auto"></div></div>;

  return (
    <div data-testid="superadmin-audit">
      <h3 className="font-bold text-base mb-4">Audit Log (Latest 100)</h3>
      <div className="border border-gray-200 max-h-[500px] overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="text-left p-2 font-semibold text-gray-600">Time</th>
              <th className="text-left p-2 font-semibold text-gray-600">Action</th>
              <th className="text-left p-2 font-semibold text-gray-600">User</th>
              <th className="text-left p-2 font-semibold text-gray-600">Details</th>
              <th className="text-left p-2 font-semibold text-gray-600">IP</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((l, i) => (
              <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="p-2 whitespace-nowrap">{l.timestamp ? new Date(l.timestamp).toLocaleString() : '—'}</td>
                <td className="p-2">
                  <span className={`px-1.5 py-0.5 font-semibold ${
                    l.action?.includes('fail') ? 'bg-red-100 text-red-700' :
                    l.action?.includes('success') ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {l.action}
                  </span>
                </td>
                <td className="p-2 text-gray-600">{l.user_id || '—'}</td>
                <td className="p-2 text-gray-500 max-w-[300px] truncate">{JSON.stringify(l.details || {})}</td>
                <td className="p-2 text-gray-400">{l.ip || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ========== PURCHASE ORDERS TAB ==========
const PurchaseOrdersTab = () => {
  const [pos, setPOs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState('all');
  const [schools, setSchools] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [checkingExpirations, setCheckingExpirations] = useState(false);
  const [newPO, setNewPO] = useState({
    school_id: '', po_number: '', po_amount: '', po_date: '',
    license_tier: 'all', license_start: '', license_expiration: '',
    renewal_status: 'active', sales_rep_name: '', sales_rep_email: '', notes: '',
  });

  useEffect(() => { fetchPOs(); fetchSchools(); }, []);

  const fetchPOs = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/purchase-orders`);
      setPOs(res.data.purchase_orders || []);
      setStats(res.data.stats || {});
    } catch { toast.error('Failed to load purchase orders'); }
    finally { setLoading(false); }
  };

  const fetchSchools = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/schools`);
      setSchools(res.data.schools || []);
    } catch {}
  };

  const handleCreate = async () => {
    if (!newPO.school_id) return toast.error('Select a school');
    if (!newPO.po_number.trim()) return toast.error('PO Number is required');
    setSaving(true);
    try {
      await axios.post(`${API}/superadmin/purchase-orders`, newPO);
      toast.success('Purchase order created');
      setNewPO({ school_id: '', po_number: '', po_amount: '', po_date: '', license_tier: 'all', license_start: '', license_expiration: '', renewal_status: 'active', sales_rep_name: '', sales_rep_email: '', notes: '' });
      setShowAdd(false);
      fetchPOs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create PO'); }
    finally { setSaving(false); }
  };

  const handleSendReminder = async (poId) => {
    setSendingReminder(poId);
    try {
      const res = await axios.post(`${API}/superadmin/purchase-orders/${poId}/send-reminder`, {});
      toast.success(`Reminder sent to ${res.data.sent_to?.length || 0} recipients`);
    } catch (e) { toast.error('Failed to send reminder'); }
    finally { setSendingReminder(null); }
  };

  const handleCheckExpirations = async () => {
    setCheckingExpirations(true);
    try {
      const res = await axios.post(`${API}/superadmin/purchase-orders/check-expirations`, {});
      toast.success(`Checked ${res.data.checked} POs. Sent ${res.data.notifications_sent} notifications.`);
      fetchPOs();
    } catch (e) { toast.error('Failed to check expirations'); }
    finally { setCheckingExpirations(false); }
  };

  const handleDelete = async (poId) => {
    if (!window.confirm('Delete this purchase order?')) return;
    try {
      await axios.delete(`${API}/superadmin/purchase-orders/${poId}`);
      toast.success('Purchase order deleted');
      fetchPOs();
    } catch { toast.error('Failed to delete'); }
  };

  const filteredPOs = filter === 'all' ? pos :
    filter === 'expiring' ? pos.filter(p => p.expiring_soon) :
    filter === 'expired' ? pos.filter(p => p.is_expired) :
    pos.filter(p => p.renewal_status === filter);

  const statusBadge = (po) => {
    if (po.is_expired) return <span className="px-2 py-0.5 text-[10px] font-bold bg-red-100 text-red-700 rounded">EXPIRED</span>;
    if (po.expiring_soon) return <span className="px-2 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700 rounded">EXPIRING SOON</span>;
    if (po.renewal_status === 'pending_renewal') return <span className="px-2 py-0.5 text-[10px] font-bold bg-orange-100 text-orange-700 rounded">PENDING RENEWAL</span>;
    if (po.renewal_status === 'trial') return <span className="px-2 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-700 rounded">TRIAL</span>;
    return <span className="px-2 py-0.5 text-[10px] font-bold bg-green-100 text-green-700 rounded">ACTIVE</span>;
  };

  if (loading) return <div className="py-16 text-center"><div className="loading-spinner mx-auto"></div></div>;

  return (
    <div data-testid="superadmin-pos">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <div className="bg-white border-2 border-gray-200 p-3 text-center">
          <div className="text-2xl font-black text-gray-800">{stats.total || 0}</div>
          <div className="text-[10px] text-gray-500 font-semibold">TOTAL POs</div>
        </div>
        <div className="bg-green-50 border-2 border-green-200 p-3 text-center">
          <div className="text-2xl font-black text-green-700">{stats.active || 0}</div>
          <div className="text-[10px] text-green-600 font-semibold">ACTIVE</div>
        </div>
        <div className="bg-amber-50 border-2 border-amber-200 p-3 text-center cursor-pointer" onClick={() => setFilter('expiring')}>
          <div className="text-2xl font-black text-amber-700">{stats.expiring_30_days || 0}</div>
          <div className="text-[10px] text-amber-600 font-semibold">EXPIRING 30 DAYS</div>
        </div>
        <div className="bg-red-50 border-2 border-red-200 p-3 text-center cursor-pointer" onClick={() => setFilter('expired')}>
          <div className="text-2xl font-black text-red-700">{stats.expired || 0}</div>
          <div className="text-[10px] text-red-600 font-semibold">EXPIRED</div>
        </div>
        <div className="bg-blue-50 border-2 border-blue-200 p-3 text-center">
          <div className="text-2xl font-black text-blue-700">${(stats.revenue_at_risk || 0).toLocaleString()}</div>
          <div className="text-[10px] text-blue-600 font-semibold">REVENUE AT RISK</div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <select value={filter} onChange={e => setFilter(e.target.value)} className="border border-gray-300 text-sm p-2 rounded" data-testid="po-filter">
            <option value="all">All POs</option>
            <option value="active">Active</option>
            <option value="expiring">Expiring Soon</option>
            <option value="expired">Expired</option>
            <option value="pending_renewal">Pending Renewal</option>
            <option value="trial">Trial</option>
          </select>
          <span className="text-xs text-gray-500">{filteredPOs.length} records</span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handleCheckExpirations} disabled={checkingExpirations} className="text-xs h-8" data-testid="check-expirations-btn">
            <Bell size={12} className="mr-1" /> {checkingExpirations ? 'Checking...' : 'Run Expiration Check'}
          </Button>
          <Button size="sm" onClick={() => setShowAdd(!showAdd)} className="bg-[#002FA7] text-white hover:bg-[#001f6f] text-xs h-8" data-testid="add-po-btn">
            <Plus size={12} className="mr-1" /> New Purchase Order
          </Button>
        </div>
      </div>

      {/* Add PO Form */}
      {showAdd && (
        <div className="border-2 border-[#002FA7] bg-blue-50/50 p-4 mb-4" data-testid="add-po-form">
          <h4 className="font-bold text-sm mb-3">New Purchase Order</h4>

          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Order Details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div>
              <Label className="text-xs">School *</Label>
              <select value={newPO.school_id} onChange={e => setNewPO({ ...newPO, school_id: e.target.value })} className="w-full mt-1 border border-gray-300 text-sm p-2 rounded" data-testid="po-school-select">
                <option value="">Select school...</option>
                {schools.map(s => (<option key={s.school_id} value={s.school_id}>{s.name}</option>))}
              </select>
            </div>
            <div>
              <Label className="text-xs">PO Number *</Label>
              <Input value={newPO.po_number} onChange={e => setNewPO({ ...newPO, po_number: e.target.value })} placeholder="e.g., PO-2026-00142" className="mt-1" data-testid="po-number-input" />
            </div>
            <div>
              <Label className="text-xs">PO Amount ($)</Label>
              <Input type="number" value={newPO.po_amount} onChange={e => setNewPO({ ...newPO, po_amount: e.target.value })} placeholder="e.g., 1200" className="mt-1" data-testid="po-amount-input" />
            </div>
            <div>
              <Label className="text-xs">PO Date</Label>
              <Input type="date" value={newPO.po_date} onChange={e => setNewPO({ ...newPO, po_date: e.target.value })} className="mt-1" />
            </div>
          </div>

          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">License</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <div>
              <Label className="text-xs">License Tier</Label>
              <select value={newPO.license_tier} onChange={e => setNewPO({ ...newPO, license_tier: e.target.value })} className="w-full mt-1 border border-gray-300 text-sm p-2 rounded">
                {Object.entries(LICENSE_TIERS).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
              </select>
            </div>
            <div>
              <Label className="text-xs">License Start</Label>
              <Input type="date" value={newPO.license_start} onChange={e => setNewPO({ ...newPO, license_start: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">License Expiration</Label>
              <Input type="date" value={newPO.license_expiration} onChange={e => setNewPO({ ...newPO, license_expiration: e.target.value })} className="mt-1" data-testid="po-expiration-input" />
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <select value={newPO.renewal_status} onChange={e => setNewPO({ ...newPO, renewal_status: e.target.value })} className="w-full mt-1 border border-gray-300 text-sm p-2 rounded">
                <option value="active">Active</option>
                <option value="trial">Trial</option>
                <option value="pending_renewal">Pending Renewal</option>
              </select>
            </div>
          </div>

          <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Assigned Sales Representative</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <Label className="text-xs">Rep Name</Label>
              <Input value={newPO.sales_rep_name} onChange={e => setNewPO({ ...newPO, sales_rep_name: e.target.value })} placeholder="e.g., John Smith" className="mt-1" data-testid="po-rep-name" />
            </div>
            <div>
              <Label className="text-xs">Rep Email</Label>
              <Input value={newPO.sales_rep_email} onChange={e => setNewPO({ ...newPO, sales_rep_email: e.target.value })} placeholder="e.g., jsmith@mosaic.com" className="mt-1" data-testid="po-rep-email" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Input value={newPO.notes} onChange={e => setNewPO({ ...newPO, notes: e.target.value })} placeholder="Optional notes" className="mt-1" />
            </div>
          </div>

          <div className="flex gap-2">
            <Button size="sm" onClick={handleCreate} disabled={saving} className="bg-[#002FA7] text-white hover:bg-[#001f6f] text-xs" data-testid="save-po-btn">
              {saving ? 'Saving...' : 'Create Purchase Order'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="text-xs">Cancel</Button>
          </div>
        </div>
      )}

      {/* PO Table */}
      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="p-3 text-xs font-semibold text-gray-500">School</th>
              <th className="p-3 text-xs font-semibold text-gray-500">PO #</th>
              <th className="p-3 text-xs font-semibold text-gray-500">Amount</th>
              <th className="p-3 text-xs font-semibold text-gray-500">Tier</th>
              <th className="p-3 text-xs font-semibold text-gray-500">Expires</th>
              <th className="p-3 text-xs font-semibold text-gray-500">Days Left</th>
              <th className="p-3 text-xs font-semibold text-gray-500">Status</th>
              <th className="p-3 text-xs font-semibold text-gray-500">Sales Rep</th>
              <th className="p-3 text-xs font-semibold text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPOs.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-gray-400">No purchase orders found. Click "New Purchase Order" to create one.</td></tr>
            )}
            {filteredPOs.map(po => (
              <tr key={po.po_id} className={`border-t border-gray-100 hover:bg-gray-50 ${po.is_expired ? 'bg-red-50/50' : po.expiring_soon ? 'bg-amber-50/50' : ''}`}>
                <td className="p-3 font-medium">{po.school_name || po.school_id}</td>
                <td className="p-3 font-mono text-xs">{po.po_number}</td>
                <td className="p-3">{po.po_amount ? `$${Number(po.po_amount).toLocaleString()}` : '—'}</td>
                <td className="p-3">
                  <span className={`text-xs px-1.5 py-0.5 font-semibold border ${LICENSE_TIERS[po.license_tier]?.color || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                    {LICENSE_TIERS[po.license_tier]?.label || po.license_tier}
                  </span>
                </td>
                <td className="p-3 text-xs">{po.license_expiration ? new Date(po.license_expiration).toLocaleDateString() : '—'}</td>
                <td className="p-3">
                  {po.days_remaining !== null ? (
                    <span className={`font-bold text-sm ${po.days_remaining <= 7 ? 'text-red-600' : po.days_remaining <= 30 ? 'text-amber-600' : po.days_remaining <= 60 ? 'text-yellow-600' : 'text-green-600'}`}>
                      {po.is_expired ? 'Expired' : `${po.days_remaining}d`}
                    </span>
                  ) : '—'}
                </td>
                <td className="p-3">{statusBadge(po)}</td>
                <td className="p-3 text-xs text-gray-600">{po.sales_rep_name || '—'}</td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Send Reminder" disabled={sendingReminder === po.po_id} onClick={() => handleSendReminder(po.po_id)} data-testid={`send-reminder-${po.po_id}`}>
                      {sendingReminder === po.po_id ? <Activity size={12} className="animate-spin" /> : <Send size={12} />}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" title="Delete" onClick={() => handleDelete(po.po_id)}>
                      <X size={12} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ========== MAIN DASHBOARD ==========
const SuperAdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [userRoleFilter, setUserRoleFilter] = useState('');

  const fetchData = async () => {
    try {
      const [statsRes, distRes] = await Promise.all([
        axios.get(`${API}/superadmin/stats`),
        axios.get(`${API}/superadmin/districts`),
      ]);
      setStats(statsRes.data);
      setDistricts(distRes.data.districts || []);
    } catch (e) {
      if (e.response?.status === 401) {
        navigate('/login');
        return;
      }
      toast.error('Failed to load platform data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-4 lg:px-6 pt-16 md:pt-8 pb-8 md:flex md:gap-8" data-testid="superadmin-dashboard">
      {/* v6.32.99 — Left sidebar nav (replaces v6.32.98 horizontal 31-tab strip). v6.34.14 — breakpoint lowered from lg≥1024 to md≥768 so tablets get the full sidebar. */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="md:flex md:gap-8 md:flex-1 md:items-start">
        <SuperAdminSidebar activeTab={activeTab} onTabChange={setActiveTab} />

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 text-sm text-red-600 mb-1">
                  <Shield size={14} />
                  <span className="font-semibold uppercase tracking-wide">Super Admin</span>
                </div>
                <h1 className="text-3xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Platform Control Center
                </h1>
                <p className="text-gray-500 mt-1">
                  Welcome back, {user?.name}. Managing {stats?.districts} districts, {stats?.schools} schools, {stats?.users?.total} users.
                </p>
              </div>
              <a href="/superadmin/guide" className="flex items-center gap-2 px-4 py-2 border-2 border-gray-200 hover:border-red-600 text-sm font-semibold transition-colors" data-testid="superadmin-guide-link">
                <FileText size={16} />
                Platform Guide
              </a>
            </div>
          </div>

          {/* TabsList retained as a hidden anchor so Radix internals + any
              external selectors keep working. The visible navigation is
              rendered by <SuperAdminSidebar/>. */}
          <TabsList className="sr-only" aria-hidden="true">
            <TabsTrigger value="overview" data-testid="sa-overview-tab-anchor">Overview</TabsTrigger>
            <TabsTrigger value="districts" data-testid="sa-districts-tab-anchor">Districts</TabsTrigger>
            <TabsTrigger value="schools" data-testid="sa-schools-tab-anchor">Schools</TabsTrigger>
            <TabsTrigger value="users" data-testid="sa-users-tab-anchor">Users</TabsTrigger>
            <TabsTrigger value="audit" data-testid="sa-audit-tab-anchor">Audit Log</TabsTrigger>
            <TabsTrigger value="pos" data-testid="sa-pos-tab-anchor">Licenses & POs</TabsTrigger>
            <TabsTrigger value="compliance" data-testid="sa-compliance-tab-anchor">ERMA / Compliance</TabsTrigger>
            <TabsTrigger value="operations" data-testid="sa-operations-tab-anchor">Operations</TabsTrigger>
            <TabsTrigger value="maintenance" data-testid="sa-maintenance-tab-anchor">Maintenance</TabsTrigger>
            <TabsTrigger value="variant-authoring" data-testid="sa-variant-authoring-tab-anchor">Variant Authoring</TabsTrigger>
            <TabsTrigger value="changelog" data-testid="sa-changelog-tab-anchor">Changelog</TabsTrigger>
            <TabsTrigger value="playbook" data-testid="sa-playbook-tab-anchor">Playbook</TabsTrigger>
            <TabsTrigger value="demo-data" data-testid="sa-demo-data-tab-anchor">Demo Data</TabsTrigger>
            <TabsTrigger value="demo-accounts" data-testid="sa-demo-accounts-tab-anchor">Demo Accounts</TabsTrigger>
            <TabsTrigger value="leads" data-testid="sa-leads-tab-anchor">Leads</TabsTrigger>
            <TabsTrigger value="beta-schools" data-testid="sa-beta-tab-anchor">Beta Schools</TabsTrigger>
            <TabsTrigger value="ab-testing" data-testid="sa-ab-tab-anchor">A/B Tests</TabsTrigger>
            <TabsTrigger value="team-updates" data-testid="sa-updates-tab-anchor">Team Updates</TabsTrigger>
            <TabsTrigger value="launch-readiness" data-testid="sa-launch-tab-anchor">Launch Readiness</TabsTrigger>
            <TabsTrigger value="print-materials" data-testid="sa-print-tab-anchor">Print (BLMs)</TabsTrigger>
            <TabsTrigger value="teacher-portal" data-testid="sa-teacher-portal-tab-anchor">Teacher Portal</TabsTrigger>
            <TabsTrigger value="download-activity" data-testid="sa-downloads-tab-anchor">Download Activity</TabsTrigger>
            <TabsTrigger value="quotes" data-testid="sa-quotes-tab-anchor">Quotes</TabsTrigger>
            <TabsTrigger value="orders" data-testid="sa-orders-tab-anchor">Orders</TabsTrigger>
            <TabsTrigger value="anomaly-report" data-testid="sa-anomaly-tab-anchor">Anomaly Report</TabsTrigger>
            <TabsTrigger value="content-studio" data-testid="sa-content-studio-tab-anchor">Content Studio</TabsTrigger>
            <TabsTrigger value="licenses" data-testid="sa-licenses-tab-anchor">Licenses</TabsTrigger>
            <TabsTrigger value="commissions" data-testid="sa-commissions-tab-anchor">Commissions</TabsTrigger>
            <TabsTrigger value="sales-collateral" data-testid="sa-collateral-tab-anchor">Sales & Marketing</TabsTrigger>
            <TabsTrigger value="big-picture-gallery" data-testid="sa-gallery-tab-anchor">Big Pictures</TabsTrigger>
            <TabsTrigger value="infrastructure" data-testid="sa-infra-tab-anchor">Infrastructure</TabsTrigger>
            <TabsTrigger value="erma-compliance" data-testid="sa-erma-tab-anchor">ERMA</TabsTrigger>
            <TabsTrigger value="benchmarking" data-testid="sa-benchmarking-tab-anchor">Benchmarking</TabsTrigger>
            <TabsTrigger value="content-auditor" data-testid="sa-content-auditor-tab-anchor">Content Auditor</TabsTrigger>
            <TabsTrigger value="coverage" data-testid="sa-coverage-tab-anchor">Coverage</TabsTrigger>
            <TabsTrigger value="print-production" data-testid="sa-print-production-tab-anchor">Print Production</TabsTrigger>
            <TabsTrigger value="summative-authoring" data-testid="sa-summative-authoring-tab-anchor">Summative Authoring</TabsTrigger>
            <TabsTrigger value="sealed-usb" data-testid="sa-sealed-usb-tab-anchor">📦 USB Orders</TabsTrigger>
          </TabsList>

          <div className="space-y-6">

        <TabsContent value="overview">
          <OverviewTab stats={stats} onRefresh={fetchData} onNavigate={(tab, roleFilter) => {
            setActiveTab(tab);
            if (tab === 'users') setUserRoleFilter(roleFilter || '');
          }} />
        </TabsContent>
        <TabsContent value="sample-demos" data-testid="sa-sample-demos-content">
          <SampleDemosTab />
        </TabsContent>
        <TabsContent value="districts">
          <DistrictsTab districts={districts} onRefresh={fetchData} />
        </TabsContent>
        <TabsContent value="schools">
          <SchoolsTab onRefresh={fetchData} />
        </TabsContent>
        <TabsContent value="users">
          <UsersTab navigate={navigate} initialRoleFilter={userRoleFilter} />
        </TabsContent>
        <TabsContent value="audit">
          <AuditTab />
        </TabsContent>
        <TabsContent value="pos">
          <PurchaseOrdersTab />
        </TabsContent>
        <TabsContent value="compliance">
          <ComplianceTab />
        </TabsContent>
        <TabsContent value="proficiency-watch" data-testid="sa-proficiency-watch-content">
          <ProficiencyWatch />
        </TabsContent>
        <TabsContent value="live-data-guard" data-testid="sa-live-data-guard-content">
          <LiveDataGuard />
        </TabsContent>
        <TabsContent value="parity-audit" data-testid="sa-parity-audit-content">
          <ParityAudit />
        </TabsContent>
        <TabsContent value="operations">
          <OperationsTab />
        </TabsContent>
        <TabsContent value="maintenance" data-testid="sa-maintenance-content">
          <MaintenanceTab />
        </TabsContent>
        <TabsContent value="audit-review" data-testid="sa-audit-review-content">
          <SemanticAuditReviewTab />
        </TabsContent>
        <TabsContent value="sales-reps" data-testid="sa-sales-reps-content">
          <SalesRepsManagementTab />
        </TabsContent>
        <TabsContent value="social-media" data-testid="sa-social-media-content">
          <SocialMediaStudioTab />
        </TabsContent>
        <TabsContent value="supplemental-solutions" data-testid="sa-supplemental-solutions-content">
          <SupplementalSolutionsTab />
        </TabsContent>
        <TabsContent value="restore-points" data-testid="sa-restore-points-content">
          <RestorePointsTab />
        </TabsContent>
        <TabsContent value="variant-authoring" data-testid="sa-variant-authoring-content">
          <VariantAuthoringTab />
        </TabsContent>
        <TabsContent value="changelog">
          <ChangelogTab />
        </TabsContent>
        <TabsContent value="playbook" data-testid="sa-playbook-content">
          <PresentationPlaybook />
        </TabsContent>
        <TabsContent value="demo-data" data-testid="sa-demo-data-content">
          <DemoDataManager />
        </TabsContent>
        <TabsContent value="demo-accounts" data-testid="sa-demo-accounts-content">
          <DemoAccountsTab />
        </TabsContent>
        <TabsContent value="leads" data-testid="sa-leads-content">
          <DemoLeadsTab />
        </TabsContent>
        <TabsContent value="beta-schools" data-testid="sa-beta-content">
          <BetaManagementTab />
        </TabsContent>
        <TabsContent value="ab-testing" data-testid="sa-ab-content">
          <ABTestingTab />
        </TabsContent>
        <TabsContent value="team-updates" data-testid="sa-updates-content">
          <TeamUpdatesTab />
        </TabsContent>
        <TabsContent value="launch-readiness" data-testid="sa-launch-content">
          <LaunchReadinessTab />
        </TabsContent>
        <TabsContent value="print-materials" data-testid="sa-print-content">
          <PrintMaterialsTab />
        </TabsContent>
        <TabsContent value="teacher-portal" data-testid="sa-teacher-portal-content">
          <TeacherPortalUsersTab />
        </TabsContent>
        <TabsContent value="download-activity" data-testid="sa-downloads-content">
          <DownloadActivityTab />
        </TabsContent>
        <TabsContent value="quotes" data-testid="sa-quotes-content">
          <QuotesManagementTab />
        </TabsContent>
        <TabsContent value="orders" data-testid="sa-orders-content">
          <OrdersTab />
        </TabsContent>
        <TabsContent value="anomaly-report" data-testid="sa-anomaly-content">
          <AnomalyReportTab />
        </TabsContent>
        <TabsContent value="content-studio" data-testid="sa-content-studio-content">
          <ContentStudioPreview />
        </TabsContent>
        <TabsContent value="licenses" data-testid="sa-licenses-content">
          <LicensesTab />
        </TabsContent>
        <TabsContent value="commissions" data-testid="sa-commissions-content">
          <CommissionTrackerTab />
        </TabsContent>
        <TabsContent value="sales-collateral" data-testid="sa-collateral-content">
          <SalesCollateralTab />
        </TabsContent>
        <TabsContent value="big-picture-gallery" data-testid="sa-gallery-content">
          <BigPictureGalleryTab />
        </TabsContent>
        <TabsContent value="infrastructure" data-testid="sa-infra-content">
          <InfrastructureGuideTab />
        </TabsContent>
        <TabsContent value="erma-compliance" data-testid="sa-erma-content">
          <ERMAComplianceTab />
        </TabsContent>
        <TabsContent value="benchmarking" data-testid="sa-benchmarking-content">
          <BenchmarkingTab />
        </TabsContent>
        <TabsContent value="content-auditor" data-testid="sa-content-auditor-content">
          <div className="space-y-6">
            <ContentAuthenticityAuditor />
            <div className="border-t border-gray-200 pt-6">
              <BPContentFitAudit />
            </div>
          </div>
        </TabsContent>
        <TabsContent value="coverage" data-testid="sa-coverage-content">
          <CoverageDashboardTab />
        </TabsContent>
        <TabsContent value="print-production" data-testid="sa-print-production-content">
          <PrintProductionTab />
        </TabsContent>
        <TabsContent value="summative-authoring" data-testid="sa-summative-authoring-content">
          <SummativeAuthoringTab />
        </TabsContent>
        {/* v6.45.7 — Sealed USB one-door hub with live Orders Queue
            counts so ops sees fulfillment pressure at a glance without
            opening the hub. Links straight into /admin/sealed-usb where
            the full queue + workflow stepper lives. */}
        <TabsContent value="sealed-usb" data-testid="sa-sealed-usb-content">
          <div style={{ background: "#FFFFFF", border: "1px solid #E4E7EC", borderRadius: 12, padding: 28 }}>
            <h3 style={{ margin: 0, fontSize: 18 }}>📦 Sealed USB · Orders &amp; Fulfillment</h3>
            <p style={{ margin: "8px 0 20px", fontSize: 13, color: "#475569", maxWidth: 620, lineHeight: 1.5 }}>
              Paid USB orders auto-flow from accepted quotes into the fulfillment queue. Open the hub to Review → Authorize → Build → QC → Pack → Ship with the 8-pill step-by-step workflow stepper.
            </p>

            {/* Live count strip */}
            <UsbOrdersQuickStats />
            <p style={{ margin: "8px 0 0", fontSize: 11, color: "#64748B", fontStyle: "italic" }}>
              💡 Click any non-zero tile above to jump straight into those orders.
            </p>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 16 }}>
              <a
                href="/admin/sealed-usb"
                data-testid="sa-sealed-usb-open-hub"
                style={{
                  padding: "12px 22px",
                  background: "#1D4ED8",
                  color: "#FFFFFF",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 700,
                  textDecoration: "none",
                }}
              >
                Open USB Orders Queue →
              </a>
              <a
                href="/admin/usbs"
                data-testid="sa-sealed-usb-open-fulfillment-hub"
                style={{
                  padding: "12px 22px",
                  background: "#002FA7",
                  color: "#FFFFFF",
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 700,
                  textDecoration: "none",
                }}
              >
                Open USB Fulfillment Hub →
              </a>
              <a
                href="/admin/sealed-usb-compatibility"
                data-testid="sa-sealed-usb-open-compat"
                style={{
                  padding: "12px 22px",
                  background: "#FFFFFF",
                  color: "#0F172A",
                  border: "1px solid #CBD5E1",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                Compatibility matrix
              </a>
              <a
                href="/usb-preview/scoring/?demo=1&rep=qc_k8"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sa-sealed-usb-open-preview"
                style={{
                  padding: "12px 22px",
                  background: "#FFFFFF",
                  color: "#0F172A",
                  border: "1px solid #CBD5E1",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                Offline preview ↗
              </a>
            </div>
          </div>
        </TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  );
};

// ========== OPERATIONS TAB ==========
const OperationsTab = () => {
  const [maintenance, setMaintenance] = useState({ enabled: false, message: '', estimated_return: '' });
  const [flags, setFlags] = useState([]);
  const [integrity, setIntegrity] = useState(null);
  const [migrations, setMigrations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [repairing, setRepairing] = useState(null);
  const [runningMigration, setRunningMigration] = useState(null);
  const [maintenanceMsg, setMaintenanceMsg] = useState('');
  const [maintenanceEta, setMaintenanceEta] = useState('');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [mRes, fRes, iRes, migRes] = await Promise.all([
        axios.get(`${API}/platform/maintenance`),
        axios.get(`${API}/platform/feature-flags`),
        axios.get(`${API}/platform/integrity-check`),
        axios.get(`${API}/platform/migrations`),
      ]);
      setMaintenance(mRes.data);
      setMaintenanceMsg(mRes.data.message || '');
      setMaintenanceEta(mRes.data.estimated_return || '');
      setFlags(fRes.data.flags || []);
      setIntegrity(iRes.data);
      setMigrations(migRes.data);
    } catch (err) { toast.error('Failed to load operations data'); }
    setLoading(false);
  };

  const toggleMaintenance = async (enabled) => {
    try {
      await axios.post(`${API}/platform/maintenance`, {
        enabled,
        message: maintenanceMsg || 'MOSAIC is undergoing scheduled maintenance. Your data is safe.',
        estimated_return: maintenanceEta,
      });
      toast.success(enabled ? 'Maintenance mode ACTIVATED' : 'Maintenance mode DEACTIVATED');
      loadAll();
    } catch (err) { toast.error('Failed to toggle maintenance'); }
  };

  const toggleFlag = async (flagId, enabled) => {
    setToggling(flagId);
    try {
      await axios.put(`${API}/platform/feature-flags/${flagId}`, { enabled });
      toast.success(`Feature ${enabled ? 'enabled' : 'disabled'}`);
      loadAll();
    } catch (err) { toast.error('Failed to toggle flag'); }
    setToggling(null);
  };

  const runRepair = async (type) => {
    if (!window.confirm(`Run repair: ${type}? This modifies live data.`)) return;
    setRepairing(type);
    try {
      const res = await axios.post(`${API}/platform/integrity-repair/${type}`, {});
      toast.success(`Repaired ${res.data.repaired} items`);
      loadAll();
    } catch (err) { toast.error('Repair failed'); }
    setRepairing(null);
  };

  const runMigration = async (version, dryRun) => {
    setRunningMigration(version);
    try {
      const res = await axios.post(`${API}/platform/migrations/${version}/run`, { dry_run: dryRun });
      if (dryRun) {
        toast.info(`Preview: ${res.data.description}`);
      } else {
        toast.success(`Migration ${version} executed in ${res.data.duration_ms}ms`);
        loadAll();
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Migration failed'); }
    setRunningMigration(null);
  };

  if (loading) return <div className="py-16 text-center"><div className="loading-spinner mx-auto"></div></div>;

  const sevColor = (s) => s === 'high' ? 'bg-red-100 text-red-800' : s === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700';

  return (
    <div className="space-y-8" data-testid="operations-tab">
      {/* Maintenance Mode */}
      <div className={`border-2 p-6 ${maintenance.enabled ? 'border-red-300 bg-red-50' : 'border-gray-100'}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold flex items-center gap-2">
            <Wrench size={16} /> Maintenance Mode
            {maintenance.enabled && <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded-full animate-pulse">ACTIVE</span>}
          </h3>
          <Button
            size="sm"
            variant={maintenance.enabled ? 'default' : 'outline'}
            className={maintenance.enabled ? 'bg-red-600 hover:bg-red-700' : ''}
            onClick={() => toggleMaintenance(!maintenance.enabled)}
            data-testid="toggle-maintenance"
          >
            {maintenance.enabled ? <><Unlock size={14} className="mr-1" /> Deactivate</> : <><Lock size={14} className="mr-1" /> Activate</>}
          </Button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          When active, students cannot start new exams or submit responses. Paused exams are preserved. Auth and admin routes remain functional.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Banner Message</Label>
            <Input
              value={maintenanceMsg}
              onChange={(e) => setMaintenanceMsg(e.target.value)}
              placeholder="Maintenance message shown to users..."
              className="mt-1"
              data-testid="maintenance-message"
            />
          </div>
          <div>
            <Label className="text-xs">Estimated Return (optional)</Label>
            <Input
              type="datetime-local"
              value={maintenanceEta}
              onChange={(e) => setMaintenanceEta(e.target.value)}
              className="mt-1"
              data-testid="maintenance-eta"
            />
          </div>
        </div>
        {maintenance.enabled && maintenance.started_at && (
          <p className="text-xs text-red-600 mt-3">Active since: {new Date(maintenance.started_at).toLocaleString()}</p>
        )}
      </div>

      {/* Feature Flags */}
      <div className="border-2 border-gray-100 p-6">
        <h3 className="font-bold flex items-center gap-2 mb-4"><Zap size={16} /> Feature Flags</h3>
        <p className="text-sm text-gray-500 mb-4">Toggle features on/off without redeployment. Disabled features return graceful errors to users.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {flags.map((f) => (
            <div key={f.flag_id} className="border border-gray-200 p-4 flex items-center justify-between" data-testid={`flag-${f.flag_id}`}>
              <div className="flex-1 mr-4">
                <div className="font-semibold text-sm">{f.name}</div>
                <div className="text-xs text-gray-500">{f.description}</div>
              </div>
              <button
                onClick={() => toggleFlag(f.flag_id, !f.enabled)}
                disabled={toggling === f.flag_id}
                className="flex-shrink-0"
                data-testid={`toggle-flag-${f.flag_id}`}
              >
                {f.enabled
                  ? <ToggleRight size={28} className="text-green-600" />
                  : <ToggleLeft size={28} className="text-gray-400" />
                }
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Data Integrity */}
      <div className="border-2 border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold flex items-center gap-2">
            <HeartPulse size={16} /> Data Integrity
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              integrity?.health === 'healthy' ? 'bg-green-100 text-green-800' :
              integrity?.health === 'warning' ? 'bg-amber-100 text-amber-800' :
              'bg-red-100 text-red-800'
            }`}>{integrity?.health?.toUpperCase()}</span>
          </h3>
          <Button size="sm" variant="outline" onClick={loadAll} data-testid="refresh-integrity">
            <HeartPulse size={14} className="mr-1" /> Re-scan
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Sessions', val: integrity?.stats?.total_sessions },
            { label: 'In Progress', val: integrity?.stats?.in_progress_sessions },
            { label: 'Paused', val: integrity?.stats?.paused_sessions },
            { label: 'Total Responses', val: integrity?.stats?.total_responses?.toLocaleString() },
          ].map(({ label, val }) => (
            <div key={label} className="border border-gray-200 p-3 text-center">
              <div className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{val ?? '—'}</div>
              <div className="text-xs text-gray-500">{label}</div>
            </div>
          ))}
        </div>

        {/* Issues */}
        {integrity?.issue_count === 0 ? (
          <div className="text-center py-6">
            <Check size={32} className="mx-auto text-green-500 mb-2" />
            <p className="text-sm text-gray-500">All integrity checks passed. No issues found.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {integrity?.issues?.map((issue) => (
              <div key={issue.type} className="border border-gray-200 p-4" data-testid={`issue-${issue.type}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sevColor(issue.severity)}`}>{issue.severity.toUpperCase()}</span>
                    <span className="font-semibold text-sm">{issue.type.replace(/_/g, ' ')}</span>
                    <span className="text-xs text-gray-400">({issue.count})</span>
                  </div>
                  {['stale_in_progress', 'broken_class_links', 'duplicate_responses'].includes(issue.type) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => runRepair(issue.type)}
                      disabled={repairing === issue.type}
                      className="text-xs h-7"
                      data-testid={`repair-${issue.type}`}
                    >
                      <Wrench size={12} className="mr-1" /> {repairing === issue.type ? 'Repairing...' : 'Auto-Repair'}
                    </Button>
                  )}
                </div>
                <p className="text-sm text-gray-600">{issue.description}</p>
                <p className="text-xs text-gray-400 mt-1">Recommendation: {issue.repair}</p>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-400 mt-3">Last scan: {integrity?.checked_at ? new Date(integrity.checked_at).toLocaleString() : '—'}</p>
      </div>

      {/* Exam Session Protection */}
      <div className="border-2 border-gray-100 p-6">
        <h3 className="font-bold flex items-center gap-2 mb-4"><Shield size={16} /> Exam Session Protection</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2"><HeartPulse size={16} className="text-green-600" /> <span className="font-semibold text-sm">Heartbeat</span></div>
            <p className="text-xs text-gray-500">Client sends progress every 30 seconds during active exams. If a student's browser crashes, their answers are preserved to the last heartbeat.</p>
          </div>
          <div className="border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2"><Database size={16} className="text-blue-600" /> <span className="font-semibold text-sm">Auto-Save</span></div>
            <p className="text-xs text-gray-500">Every individual response is saved immediately to the database. No batch submissions — nothing is lost if the session ends unexpectedly.</p>
          </div>
          <div className="border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2"><Pause size={16} className="text-amber-600" /> <span className="font-semibold text-sm">Crash Recovery</span></div>
            <p className="text-xs text-gray-500">Students returning after a disconnect see "Resume where you left off" with all previous answers intact. Sessions never auto-delete.</p>
          </div>
        </div>
      </div>

      {/* Database Migrations */}
      <div className="border-2 border-gray-100 p-6">
        <h3 className="font-bold flex items-center gap-2 mb-4"><Database size={16} /> Database Migrations</h3>
        <p className="text-sm text-gray-500 mb-4">Versioned schema changes. Preview (dry-run) before executing. All migrations are logged in the audit trail.</p>
        {migrations?.total === 0 ? (
          <p className="text-sm text-gray-400 py-4">No migrations available.</p>
        ) : (
          <div className="space-y-3">
            {migrations?.migrations?.map((m) => (
              <div key={m.version} className="border border-gray-200 p-4 flex items-center justify-between" data-testid={`migration-${m.version}`}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded">{String(m.version).padStart(3, '0')}</span>
                    <span className="font-semibold text-sm">{m.name.replace(/_/g, ' ')}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${m.status === 'executed' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                      {m.status}
                    </span>
                  </div>
                  {m.executed_at && <p className="text-xs text-gray-400 mt-1">Executed: {new Date(m.executed_at).toLocaleString()} ({m.duration_ms}ms)</p>}
                </div>
                {m.status === 'pending' && (
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => runMigration(m.version, true)} disabled={runningMigration === m.version} className="text-xs h-7" data-testid={`preview-migration-${m.version}`}>
                      <Eye size={12} className="mr-1" /> Preview
                    </Button>
                    <Button size="sm" onClick={() => { if (window.confirm(`Execute migration ${m.version}? This modifies the database.`)) runMigration(m.version, false); }} disabled={runningMigration === m.version} className="text-xs h-7" data-testid={`run-migration-${m.version}`}>
                      <Play size={12} className="mr-1" /> Execute
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ========== COMPLIANCE / ERMA TAB ==========
const ComplianceTab = () => {
  const [policy, setPolicy] = useState(null);
  const [coppaStudents, setCoppaStudents] = useState([]);
  const [breachReports, setBreachReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purging, setPurging] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editPolicy, setEditPolicy] = useState({});

  useEffect(() => {
    loadComplianceData();
  }, []);

  const loadComplianceData = async () => {
    setLoading(true);
    try {
      const [policyRes, coppaRes, breachRes] = await Promise.all([
        axios.get(`${API}/compliance/retention-policy`),
        axios.get(`${API}/compliance/coppa/students`),
        axios.get(`${API}/compliance/breach-reports`),
      ]);
      setPolicy(policyRes.data);
      setEditPolicy(policyRes.data);
      setCoppaStudents(coppaRes.data.students || []);
      setBreachReports(breachRes.data.reports || []);
    } catch (err) {
      toast.error('Failed to load compliance data');
    }
    setLoading(false);
  };

  const savePolicy = async () => {
    try {
      await axios.put(`${API}/compliance/retention-policy`, editPolicy);
      toast.success('Retention policy updated');
      setEditing(false);
      loadComplianceData();
    } catch (err) {
      toast.error('Failed to update policy');
    }
  };

  const runPurge = async () => {
    if (!window.confirm('This will permanently delete data older than retention thresholds. Continue?')) return;
    setPurging(true);
    try {
      const res = await axios.post(`${API}/compliance/purge-expired`, {});
      toast.success(`Purge complete: ${JSON.stringify(res.data.purged)}`);
      loadComplianceData();
    } catch (err) {
      toast.error('Purge failed');
    }
    setPurging(false);
  };

  const recordConsent = async (studentId, consent) => {
    try {
      await axios.post(`${API}/compliance/coppa/consent/${studentId}`, { consent });
      toast.success(consent ? 'Consent recorded' : 'Consent revoked');
      loadComplianceData();
    } catch (err) {
      toast.error('Failed to record consent');
    }
  };

  if (loading) return <div className="py-16 text-center"><div className="loading-spinner mx-auto"></div></div>;

  return (
    <div className="space-y-8" data-testid="compliance-tab">
      {/* ERMA Readiness Banner */}
      <div className="bg-gradient-to-r from-[#002FA7] to-[#0f766e] text-white p-6 flex items-center gap-4">
        <Shield size={32} />
        <div>
          <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>ERMA Compliance Center</h2>
          <p className="text-sm opacity-80">NYCPS data privacy and security compliance management</p>
        </div>
      </div>

      {/* Data Retention Policy */}
      <div className="border-2 border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold flex items-center gap-2"><Calendar size={16} /> Data Retention Policy</h3>
          <div className="flex items-center gap-2">
            {editing ? (
              <>
                <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                <Button size="sm" onClick={savePolicy} data-testid="save-retention-policy"><Save size={14} className="mr-1" /> Save</Button>
              </>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-retention-policy">
                <Edit2 size={14} className="mr-1" /> Edit
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { key: 'student_data_retention_years', label: 'Student Data (years)', icon: Users },
            { key: 'audit_log_retention_years', label: 'Audit Logs (years)', icon: FileText },
            { key: 'session_data_retention_days', label: 'Sessions (days)', icon: Activity },
            { key: 'deleted_user_purge_days', label: 'Disabled User Purge (days)', icon: UserX },
          ].map(({ key, label, icon: Icon }) => (
            <div key={key} className="border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-xs text-gray-500 mb-2"><Icon size={14} /> {label}</div>
              {editing ? (
                <Input
                  type="number"
                  value={editPolicy[key] || ''}
                  onChange={(e) => setEditPolicy({ ...editPolicy, [key]: parseInt(e.target.value) || 0 })}
                  className="h-8"
                  data-testid={`retention-${key}`}
                />
              ) : (
                <div className="text-2xl font-black" style={{ fontFamily: 'Outfit, sans-serif' }}>{policy?.[key] || '—'}</div>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
          <div className="text-xs text-gray-500">
            Auto-purge: <span className={policy?.auto_purge_enabled ? 'text-green-700 font-bold' : 'text-gray-400'}>{policy?.auto_purge_enabled ? 'Enabled' : 'Disabled'}</span>
            {policy?.last_purge_run && <span className="ml-4">Last run: {new Date(policy.last_purge_run).toLocaleDateString()}</span>}
          </div>
          <Button size="sm" variant="outline" onClick={runPurge} disabled={purging} data-testid="run-purge-btn">
            <AlertTriangle size={14} className="mr-1" /> {purging ? 'Purging...' : 'Run Manual Purge'}
          </Button>
        </div>
      </div>

      {/* COPPA Consent Management */}
      <div className="border-2 border-gray-100 p-6">
        <h3 className="font-bold flex items-center gap-2 mb-4"><UserCheck size={16} /> COPPA Consent (Students Under 13)</h3>
        <p className="text-sm text-gray-500 mb-4">Students in grades K-5 require documented parental consent under COPPA.</p>
        {coppaStudents.length === 0 ? (
          <p className="text-sm text-gray-400 py-4">No COPPA-applicable students found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="coppa-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 font-semibold">Student</th>
                  <th className="text-left p-3 font-semibold">Grade(s)</th>
                  <th className="text-left p-3 font-semibold">Consent</th>
                  <th className="text-left p-3 font-semibold">Date</th>
                  <th className="text-right p-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {coppaStudents.slice(0, 50).map((s) => (
                  <tr key={s.user_id} className="border-b border-gray-100">
                    <td className="p-3">{s.name}</td>
                    <td className="p-3">{s.grades?.join(', ')}</td>
                    <td className="p-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.coppa_consent ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                        {s.coppa_consent ? 'Granted' : 'Pending'}
                      </span>
                    </td>
                    <td className="p-3 text-gray-500 text-xs">{s.coppa_consent_date ? new Date(s.coppa_consent_date).toLocaleDateString() : '—'}</td>
                    <td className="p-3 text-right">
                      {s.coppa_consent ? (
                        <Button size="sm" variant="outline" onClick={() => recordConsent(s.user_id, false)} className="text-red-600 text-xs h-7" data-testid={`revoke-consent-${s.user_id}`}>
                          Revoke
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => recordConsent(s.user_id, true)} className="text-xs h-7" data-testid={`grant-consent-${s.user_id}`}>
                          <Check size={12} className="mr-1" /> Grant
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-gray-400 mt-2">{coppaStudents.length} COPPA-applicable students total</p>
          </div>
        )}
      </div>

      {/* Breach Reports */}
      <div className="border-2 border-gray-100 p-6">
        <h3 className="font-bold flex items-center gap-2 mb-4"><AlertTriangle size={16} /> Breach Incident Log</h3>
        {breachReports.length === 0 ? (
          <div className="text-center py-8">
            <Shield size={32} className="mx-auto text-green-500 mb-2" />
            <p className="text-sm text-gray-500">No breach incidents reported. MOSAIC is operating securely.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {breachReports.map((r) => (
              <div key={r.id} className="border border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sm">{r.description?.slice(0, 80)}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.severity === 'high' ? 'bg-red-100 text-red-800' : r.severity === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}>{r.severity}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Reported: {new Date(r.reported_at).toLocaleString()} | Records affected: {r.affected_records} | NYCPS notified: {r.nycps_notified ? 'Yes' : 'No'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Links */}
      <div className="border-2 border-gray-100 p-6">
        <h3 className="font-bold flex items-center gap-2 mb-2"><FileText size={16} /> ERMA Submission Packet</h3>
        <p className="text-sm text-gray-500 mb-4">Downloadable legal documents for your NYCPS ERMA vendor vetting submission. Open each and use Ctrl/Cmd + P to save as PDF.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <a href="/compliance/erma-cover-letter.html" target="_blank" rel="noopener noreferrer" className="border-2 border-gray-200 p-5 hover:border-[#002FA7] transition-colors group flex items-start gap-4 md:col-span-2" data-testid="download-cover-letter">
            <div className="w-10 h-10 bg-[#0f766e] text-white flex items-center justify-center flex-shrink-0">
              <Send size={18} />
            </div>
            <div>
              <div className="font-bold text-sm group-hover:text-[#002FA7] transition-colors">ERMA Cover Letter</div>
              <div className="text-xs text-gray-500 mt-1">Formal submission letter to NYCPS DIIT/ERMA Review Committee. Includes compliance summary table, platform stats, enclosure list, and signature block. Customize and print as PDF.</div>
            </div>
          </a>
          <a href="/compliance/data-privacy-agreement.html" target="_blank" rel="noopener noreferrer" className="border-2 border-gray-200 p-5 hover:border-[#002FA7] transition-colors group flex items-start gap-4" data-testid="download-dpa">
            <div className="w-10 h-10 bg-[#002FA7] text-white flex items-center justify-center flex-shrink-0">
              <Lock size={18} />
            </div>
            <div>
              <div className="font-bold text-sm group-hover:text-[#002FA7] transition-colors">Data Privacy Agreement (DPA)</div>
              <div className="text-xs text-gray-500 mt-1">Formal contract template between Lerno Co. and the Educational Agency. Covers FERPA, COPPA, Ed Law 2-d, data elements, retention, breach notification, parental rights, subprocessors.</div>
            </div>
          </a>
          <a href="/compliance/incident-response-plan.html" target="_blank" rel="noopener noreferrer" className="border-2 border-gray-200 p-5 hover:border-[#002FA7] transition-colors group flex items-start gap-4" data-testid="download-irp">
            <div className="w-10 h-10 bg-dc2626 text-white flex items-center justify-center flex-shrink-0" style={{ background: '#dc2626' }}>
              <AlertTriangle size={18} />
            </div>
            <div>
              <div className="font-bold text-sm group-hover:text-[#002FA7] transition-colors">Incident Response Plan</div>
              <div className="text-xs text-gray-500 mt-1">Security incident response procedures aligned to NIST SP 800-61. Includes 5-phase response framework, severity matrix, notification templates, IRT roles, and quick-reference checklist.</div>
            </div>
          </a>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <a href="/privacy" className="border-2 border-gray-100 p-5 hover:border-[#002FA7] transition-colors group" data-testid="compliance-privacy-link">
          <Shield size={20} className="text-gray-400 group-hover:text-[#002FA7] mb-2" />
          <div className="font-bold text-sm">Privacy Policy</div>
          <div className="text-xs text-gray-500">Public-facing privacy documentation</div>
        </a>
        <a href="/terms" className="border-2 border-gray-100 p-5 hover:border-[#002FA7] transition-colors group" data-testid="compliance-terms-link">
          <FileText size={20} className="text-gray-400 group-hover:text-[#002FA7] mb-2" />
          <div className="font-bold text-sm">Terms of Service</div>
          <div className="text-xs text-gray-500">Platform usage terms</div>
        </a>
        <a href="/subprocessors" className="border-2 border-gray-100 p-5 hover:border-[#002FA7] transition-colors group" data-testid="compliance-subprocessors-link">
          <Activity size={20} className="text-gray-400 group-hover:text-[#002FA7] mb-2" />
          <div className="font-bold text-sm">Subprocessor List</div>
          <div className="text-xs text-gray-500">Third-party service disclosures</div>
        </a>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;

// ========== CHANGELOG TAB ==========
const CATEGORY_STYLES = {
  feature: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', label: 'Feature' },
  fix: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Fix' },
  ui: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', label: 'UI' },
  docs: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', label: 'Docs' },
  security: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Security' },
};

const TYPE_BADGE = {
  major: { bg: 'bg-red-100 text-red-800', label: 'Major' },
  minor: { bg: 'bg-blue-100 text-blue-800', label: 'Minor' },
  patch: { bg: 'bg-gray-100 text-gray-700', label: 'Patch' },
};

const ChangelogTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedVersion, setExpandedVersion] = useState(null);

  useEffect(() => {
    const fetchChangelog = async () => {
      try {
        const res = await axios.get(`${API}/platform/changelog`);
        setData(res.data);
        if (res.data.releases?.length > 0) {
          setExpandedVersion(res.data.releases[0].version);
        }
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    fetchChangelog();
  }, []);

  if (loading) return <div className="py-16 text-center text-gray-400">Loading changelog...</div>;
  if (!data) return <div className="py-16 text-center text-gray-400">Unable to load changelog.</div>;

  return (
    <div className="space-y-6" data-testid="changelog-tab">
      {/* Current Version Header */}
      <div className="border-2 border-[#002FA7] bg-[#002FA7]/5 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#002FA7] flex items-center justify-center">
              <Tag size={24} className="text-white" />
            </div>
            <div>
              <div className="text-sm text-gray-500 font-medium">Current Version</div>
              <div className="text-3xl font-black" style={{ fontFamily: 'Outfit, sans-serif', color: '#002FA7' }}>
                v{data.current_version}
              </div>
              <div className="text-sm text-gray-600 font-semibold">{data.current_name}</div>
            </div>
          </div>
          <div className="text-right text-sm text-gray-500">
            <div>{data.releases?.length || 0} releases</div>
            <div>{data.releases?.reduce((sum, r) => sum + r.changes.length, 0) || 0} total changes</div>
          </div>
        </div>
      </div>

      {/* Release Timeline */}
      <div className="relative" data-testid="changelog-timeline">
        {/* Vertical line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />

        {data.releases?.map((release, idx) => {
          const isExpanded = expandedVersion === release.version;
          const typeBadge = TYPE_BADGE[release.type] || TYPE_BADGE.minor;
          const isCurrent = idx === 0;

          return (
            <div key={release.version} className="relative pl-16 pb-8" data-testid={`changelog-release-${release.version}`}>
              {/* Timeline dot */}
              <div className={`absolute left-4 w-5 h-5 rounded-full border-2 ${isCurrent ? 'bg-[#002FA7] border-[#002FA7]' : 'bg-white border-gray-300'}`} />

              {/* Release card */}
              <div
                className={`border-2 ${isCurrent ? 'border-[#002FA7]/30' : 'border-gray-200'} cursor-pointer hover:border-gray-300 transition-colors`}
                onClick={() => setExpandedVersion(isExpanded ? null : release.version)}
              >
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold font-mono" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      v{release.version}
                    </span>
                    <span className={`text-xs font-semibold px-2 py-0.5 ${typeBadge.bg}`}>
                      {typeBadge.label}
                    </span>
                    <span className="text-sm text-gray-500">{release.name}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-500">
                    <span>{release.date}</span>
                    <span className="text-xs bg-gray-100 px-2 py-0.5">{release.changes.length} changes</span>
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>
                <div className="px-4 pb-3 text-sm text-gray-600">{release.summary}</div>
              </div>

              {/* Expanded changes */}
              {isExpanded && (
                <div className="mt-2 space-y-1.5" data-testid={`changelog-changes-${release.version}`}>
                  {release.changes.map((change, cIdx) => {
                    const catStyle = CATEGORY_STYLES[change.category] || CATEGORY_STYLES.feature;
                    return (
                      <div key={cIdx} className={`flex items-start gap-3 p-3 border ${catStyle.border} ${catStyle.bg}`}>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 ${catStyle.text} whitespace-nowrap`}>
                          {catStyle.label}
                        </span>
                        <span className="text-sm text-gray-700">{change.description}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* v6.34.15 — Smart install banner (hidden when already installed as PWA). */}
      <InstallAppBanner />
    </div>
  );
};
